/**
 * The launchpad's words, in English, Simplified Chinese, Vietnamese and Hindi.
 *
 * The boring kind of i18n: one typed dictionary per language, a tiny renderer
 * helper, and a stored preference honored on load. Static markup carries
 * data-i18n attributes that applyStatic() fills in; dynamic markup calls
 * t()/tr() at render time. The switch itself belongs to the caller: it stores
 * the choice and applies it in place, re-rendering the surfaces built from
 * live data.
 */

export type Lang = "en" | "zh" | "vi" | "hi";

/** The localStorage key holding the visitor's language choice. */
export const LANG_KEY = "od-obrain-lang";

/** The slice of Storage the preference needs (fakes stay small in tests). */
export type LangStore = Pick<Storage, "getItem" | "setItem">;

// ---- the English copy: the source of truth for keys ------------------------

export const en = {
  title: "OBRAIN · First Flybrain Onchain",
  skip: "Skip to content",
  "nav.aria": "Primary",
  "nav.brain": "Brain",
  "docs.index": "Documentation",
  "docs.onThisPage": "On this page",
  "docs.prev": "Previous",
  "docs.next": "Next",
  "docs.kicker": "THE DOCS",
  "docs.title": "Read the source of truth",
  "docs.aria": "Documentation",
  "nav.docs": "Docs",
  "nav.flybookAria": "Flybook, the colony - opens in a new tab",
  "lang.aria": "Language",
  "wallet.connect": "Connect wallet",
  "wallet.title": "Connect an Arc 5042 wallet",
  "brain.aria": "Brain - the live view",
  "brain.tick": "Tick",
  "stage.figNote": "fig. 1 · the brain, thinking · dorsal projection, BANC v888",
  "stage.legend":
    "Specimen: the intact central nervous system of an adult female <i>Drosophila melanogaster</i>, executing in Arc consensus - 169,088 cells, 325,292 synaptic records etched as bytecode. Every flash on this stage is a mined <code>BrainState</code> event, and the organism's entire emitted history replays off-chain at 74/74 spike lists, <code>stateRoot()</code> byte-isomorphic. Field record CS-001: 590 spikes from 192 cells, all within the sixty-column sensory epithelium.",
  "tape.label": "Thought tape",
  "tape.demo1": "TICK <b>4,087</b> · THOUGHT <b>#311</b> · <b>141</b> FIRED · LOBULA · LEFT",
  "tape.demo2": "FEED ACCEPTED · TICK <b>4,086</b> · <b>203</b> NEURONS FIRED · MEDULLA · RIGHT",
  "tape.demo3": "TICK <b>4,085</b> · <b>96</b> FIRED · 25 OBRAIN BURNED",
  "vit.aria": "The brain state, read from the chain",
  "vit.kicker": "THE STATE",
  "vit.title": "What the brain is",
  "vit.age": "Age · thoughts so far",
  "vit.ageSub": "one thought per feed, each thought a tick",
  "vit.lastThought": "The last thought",
  "vit.spiked": "neurons fired",
  "vit.synapses": "synapses",
  "vit.spikedSub": "of {total} in the brain · {delta} against the thought before",
  "vit.spikedSubFirst": "of {total} · the newest thought in the window",
  "vit.spikedSubAlone": "the newest thought in the window",
  "vit.spikedSubAloneBefore": "{delta} against the thought before",
  "vit.synapsesSub": "{delta} against the thought before",
  "vit.synapsesSubFirst": "crossed in that one thought",
  "vit.field": "The field · membrane charge",
  "vit.chargedSub": "cells charged, of {total} · {gained} gained since the last read",
  "vit.peak": "peak charge",
  "vit.above": "at the trigger",
  "vit.peakSub": "the closest cell · the kernel fires at {trigger}",
  "vit.fieldWaiting": "reading the field from the node…",
  "vit.sense": "The sense · the last poke",
  "vit.senseChannel": "channel",
  "vit.senseDepth": "charge of 255",
  "vit.senseNone": "no feed in the window yet",
  "vit.senseLight": "a light touch on one channel",
  "vit.senseMost": "most of the way to threshold",
  "vit.senseFires": "that channel fires at once",
  "vit.root": "State root",
  "vit.rootSub": "the fingerprint every thought writes, at tick {tick}",
  "vit.wiring": "Wiring · BANC v888",
  "vit.wiringValue": "{cells} cells · {tapes} tapes",
  "vit.sealSealed": "sealed · the wiring can no longer change",
  "vit.sealOpen": "open · tapes can still be published",
  "vit.noteMine": "your fee · the brain thought at tick {tick} · {fired} neurons fired",
  "vit.notePoke": "your fee · channel {c} charged {d}/255 · the thought is being made",
  "vit.noteThought": "the brain thought at tick {tick} · {fired} neurons fired",
  "vit.reading": "reading the brain from the chain…",
  "vit.tabsAria": "The brain state",
  "vit.tabState": "The state",
  "vit.tabLog": "The log",
  "vit.logHint": "open a thought to read the BrainState event it left",
  "vit.logEmpty": "no thought in the window yet",
  "vit.logTick": "tick {tick}",
  "vit.logMeta": "{fired} fired · {synapses} synapses",
  "vit.logRowLabel": "Thought at tick {tick}: {fired} neurons fired, {synapses} synapses crossed",
  "vit.dTick": "indexed tick",
  "vit.dReadout": "readout · neurons 0-3",
  "vit.dFired": "fired neurons",
  "vit.dSpiked": "the kernel's spike count",
  "vit.dSpikedOf": "neurons fired",
  "vit.dSynapses": "synapses crossed",
  "vit.dCapped": "spike cap",
  "vit.dCappedYes": "hit · the tick was truncated",
  "vit.dCappedNo": "not reached",
  "vit.dPoke": "sensory poke",
  "vit.dPokeNone": "none · the kernel was not handed one",
  "vit.dPokeValue": "channel {c} · charge {d}/255",
  "vit.dBlock": "block",
  "vit.dTx": "transaction {tx} ↗",
  "vit.quiet": "no thought in the window yet - the brain thinks when it is fed",
  "feed.aria": "Feed the brain - pay the fee in OBRAIN",
  "feed.kicker": "THE FEED",
  "feed.title": "Feed the brain",
  "feed.channel": "Sensory channel",
  "feed.tier": "Feed tier",
  "feed.burn": "Feed",
  "feed.balance": "Balance",
  "feed.approve": "Approve OBRAIN",
  "feed.ready": "Feed the brain",
  "feed.buy": "Buy OBRAIN",
  "feed.buyAria": "Buy OBRAIN - opens in a new tab",
  "feed.log": "Feed log",
  "feed.logRow": "{n} OBRAIN · channel {c}",
  "feed.logEmpty": "No feed yet - the first fee opens the first thought.",
  "feed.insufficient": "You need {n} more OBRAIN - buy it on the market",
  "feed.grant":
    "One approval covers every later feed: the brain can only ever send OBRAIN to the dead address.",
  "feed.confirm": "Confirm in your wallet…",
  "feed.approving": "Approving OBRAIN…",
  "feed.feeding": "Paying {n} OBRAIN…",
  "feed.pending": "Fee paid · waiting for the thought…",
  "feed.landed": "Fee paid · the index is behind tonight - the tape will show the thought",
  "feed.reading": "Reading your OBRAIN…",
  "feed.readChain": "Reading the brain's own prices…",
  "feed.readFailed": "Could not read your OBRAIN balance: {reason}",
  "feed.noWallet": "Connect your wallet - the button at the top right - to pay the fee from it.",
  "feed.readyNote": "Pay {n} OBRAIN from your wallet - the brain thinks in the same transaction.",
  "feed.thought": "Thought accepted · tick <b>{tick}</b> · <b>{fired}</b> neurons fired",
  "feed.thoughtUndecoded": "Fee accepted · tick <b>{tick}</b> · waiting for the fired set",
  "feed.failed": "Feed failed: {reason}",
  "feed.unknownError": "the wallet refused without a reason",
  "feed.charge0": "31/255 charge · a light touch on one channel",
  "feed.charge1": "127/255 charge · most of the way to threshold",
  "feed.charge2": "255/255 charge · that channel fires at once",
  "hatch.aria": "Hatch - the launchpad",
  "hatch.kicker": "THE LAUNCHPAD",
  "hatch.title": "Hatch a specimen",
  "hatch.sub": "Three steps · you sign only at the end",
  "hatch.intro1": "Tonight the brain has fired",
  "hatch.intro2":
    "neurons. Each fired neuron may be hatched once, ever. Review calmly; signing happens only in your own wallet.",
  "hatch.s1t": "Pick a fired neuron",
  "hatch.s1s": "From tonight's window",
  "hatch.s2t": "Review the specimen",
  "hatch.s2s": "Plate & provenance",
  "hatch.s3t": "Mint the card",
  "hatch.s3s": "One press · two signatures",
  "hatch.step1Sub": "Tonight's window · newest first · one hatch per neuron, ever",
  "hatch.firedCount": "{n} neurons shown",
  "th.art": "Art",
  "th.id": "N-id",
  "th.binom": "Latin binomial",
  "th.region": "Region · side · cell",
  "th.pick": "Pick",
  "pick.done": "Picked",
  "hatch.step1Hint": "Pick a row to continue · nothing is reserved yet",
  "hatch.step1Next": "Continue to review",
  "hatch.step2Sub": "Plate composed from the genome · read once, keep forever",
  "hatch.back": "Back",
  "hatch.step2Next": "Continue to hatch",
  "launch.sub": "One neuron · one card · forever",
  "launch.chipCard": "SPECIMEN CARD",
  "launch.cardK": "What you get",
  "launch.cardV": "<b>One card</b>, forever",
  "launch.cardS":
    "The card carries the specimen's biology on chain: its region, its cell class, and what its thought did.",
  "launch.pendCard": "Minted with your press",
  "launch.costS": "Gas only. The card contract charges nothing.",
  "launch.claimS": "The row the collection reads, written right after the mint.",
  "launch.whoK": "Who can sign",
  "launch.whoV": "<b>{addr}</b> - the thought's feeder (D5).",
  "launch.whoS": "Only the wallet that fed the thought can mint this card. Nobody else.",
  "launch.after": "After the mint",
  "launch.viewTokenAria": "The specimen's card on the Arc explorer",
  "launch.viewTxAria": "The mint transaction on the Arc explorer",
  "launch.bandOpenedK": "Ready to mint",
  "launch.bandOpenedT":
    "The plate is read from the genome: the neuron, the card contract, the cost and the claim. Pressing it is what signs - nothing is sent from this page.",
  "launch.noteOpened":
    "Nothing on this plate is missing. The press mints the card; nothing is signed here.",
  "launch.noteRefused": "The press is still live. A refusal costs nothing and can be repeated.",
  "launch.bandWalletK": "Wallet refused",
  "launch.bandWalletS": "Nothing signed",
  "launch.bandNoWalletK": "No wallet connected",
  "launch.bandNoWalletT":
    "Hatching is paid by the wallet that fed the thought, so it needs a wallet to exist. The plate is complete without one.",
  "launch.bandWrongWalletK": "Not the feeder wallet",
  "launch.bandWrongWalletT":
    "The wallet in this browser did not feed this thought, so it cannot pay for its hatch or sign for it.",
  "launch.bandSigningK": "Confirm in your wallet",
  "launch.bandSigningT":
    "Your wallet is asking for approval. This page waits, and sends nothing on its own.",
  "launch.noteSigning": "Read the payload line by line before you confirm.",
  "launch.bandLaunchedK": "Card minted · claim pending",
  "launch.bandLaunchedT":
    "The token exists and the claim is still unsigned. Sign it to write the specimen into the registry.",
  "launch.noteLaunched": "Two signatures, two steps: mint the card, then claim it.",
  "launch.bandClaimedK": "Card minted",
  "launch.bandClaimedT": "{binom} · {id} - one card per neuron, for life.",
  "launch.noteClaimed": "One card per neuron, for life - the registry numbers them in order.",
  "launch.bandUnconfirmedK": "Mint not confirmed",
  "launch.bandUnconfirmedS": "Nothing claimed",
  "launch.go": "Mint the card",
  "launch.confirm": "Confirm in your wallet…",
  "launch.claim": "Sign the claim",
  "launch.claiming": "Confirm the claim in your wallet…",
  "launch.claimNote": "Card minted · one more signature and it is in the collection.",
  "launch.claimFailed": "Card minted · claim failed: {reason}",
  "launch.claimed": "Card minted",
  "launch.viewToken": "card",
  "launch.viewTx": "tx",
  "launch.unconfirmed": "The card at {addr} is not confirmed · tx {tx}",
  "launch.failed": "Wallet refused: {reason}",
  "launch.noWallet": "Connect your wallet to mint the card.",
  "launch.notCreator": "Only the feeder wallet can mint this card: {addr}",
  "launch.feederUnknown":
    "This thought has no feeder wallet, so only that feeder could hatch it (D5).",
  "hlist.aria": "Hatched specimens",
  "hlist.kicker": "THE HATCHED REGISTER",
  "hlist.title": "The hatched register",
  "hlist.sub": "One fired neuron, one card, forever. Every row is a registry claim read off Arc.",
  "hlist.count": "<b>{n}</b> hatched",
  "hlist.token": "Card",
  "hlist.market": "Chain",
  "hlist.open": "Open on chain",
  "hlist.openAria": "Open {name} on the Arc explorer - opens in a new tab",
  "hlist.empty":
    "Nothing has hatched yet. Feed the brain, pick a fired neuron, and prepare it calmly.",
  "col.aria": "Permanent collection",
  "col.kicker": "THE PERMANENT COLLECTION",
  "col.title": "Hatched specimens",
  "col.sub": "Every card carries a registry claim, verified offchain",
  "col.reading": "reading the registry…",
  "col.empty":
    "The registry read is complete: no claims yet. The collection begins with your first hatch. Feed the brain, pick a fired neuron, and prepare it calmly.",
  "facts.seed": "Seed",
  "facts.fired": "Fired set",
  "facts.spiked": "Spiked",
  "facts.synapses": "Synapses",
  "facts.type": "Cell type",
  "facts.class": "Cell class",
  "facts.flow": "Flow",
  "facts.side": "Side",
  "facts.region": "Region",
  "hatch.gridAria": "Fired neurons, as cards",
  "filter.legacy": "Founding",
  "filter.rare": "Rare",
  "filter.uncommon": "Uncommon",
  "filter.common": "Common",
  "filter.all": "All tiers",
  "filter.result": "<b>{shown}</b> of {total} cards",
  "filter.emptyRegion": "Nothing in region {region} matches the tier you picked.",
  "filter.emptyTier": "Nothing in tier {tier} yet - the filter is honest, the shelf is empty.",
  "filter.clearFilters": "Clear filters",
  "filter.empty": "No specimens match this filter",
  "filter.allRegions": "All regions",
  "filter.region": "Region",
  "filter.tierLevel": "Tier level",
  "filter.clear": "Clear",
  "filter.title": "Filter",
  "col.buy": "Card ↗",
  "col.countAll": "<b>{hatched}</b> hatched · of <b>{total}</b> possible",
  "col.countRegion":
    "<b>{shown}</b> in {region} · of <b>{hatched}</b> hatched · <b>{total}</b> possible",
  "bld.aria": "Build on the tapes - the onchain connectome",
  "bld.kicker": "THE BUILD",
  "bld.title": "Inherit the 85 tapes",
  "bld.sub": "The brain's synapses are onchain, as contract code anyone can read",
  "bld.countLabel": "TAPES_COUNT()",
  "bld.what.h": "What a tape is",
  "bld.what.p1":
    "One <b>ImmortalFruitFliesTape</b> contract holds one slice of the connectome: its constructor returns the synapse payload, so <b>eth_getCode(tape) is the data</b>. Eighty-five of them carry the whole spiking network, registered in the brain's own index - there is no server to ask, only a chain to read.",
  "bld.what.p2":
    "They survive this website. As long as Arc keeps state, the connectome is readable by a script, a spreadsheet, a model, or a kernel you deploy yourself.",
  "bld.index": "On this page",
  "bld.kv.brain": "Brain",
  "bld.kv.payload": "Payload",
  "bld.kv.payloadV": "runtime code at tapes(i), byte for byte",
  "bld.kv.slots": "Slots · sources",
  "bld.kv.slotsV": "169,088 slots · 169,078 carry outgoing synapses",
  "bld.kv.sparse": "Density",
  "bld.kv.sparseV": "top-2 strongest synapses per pair, from BANC v888",
  "bld.manifest.h": "The 85, as the chain has them",
  "bld.manifest.dl": "Download the manifest",
  "bld.manifest.reading": "reading the tapes from the index…",
  "bld.manifest.count": "{n} tapes · brain {brain} · walked once, served from the index",
  "bld.manifest.empty": "no tapes in the index yet: it walks them from the chain once on start",
  "bld.th.index": "#",
  "bld.th.address": "Tape contract",
  "bld.th.start": "First neuron",
  "bld.th.bytes": "Bytes",
  "bld.th.sha": "sha256",
  "bld.decode.label": "Decode one tape in this browser",
  "bld.decode.go": "Decode",
  "bld.decode.out":
    "tape {index}: {neurons} source neurons · {synapses} synapses · {targets} distinct targets · weights {lightest}…{heaviest}",
  "bld.decode.error": "cannot decode tape {index}: {reason}",
  "bld.layout.h": "What one tape contains",
  "bld.layout.p":
    "Big-endian throughout: a 16-bit offset table, then five bytes per synapse. The first uint16 doubles as the header, because it is also the first offset.",
  "bld.layout.p2":
    "The layout is written by <code>tools/tapes.py</code> and decoded by <code>web/src/tapes.ts</code> - the same rules, one in Python and one in the browser, which is what the button above runs.",
  "bld.needs.h": "What else a faithful run needs",
  "bld.needs.p":
    "The tapes are the connectome, not the brain state. These come from the same contract, and each one changes what a tick does:",
  "bld.n.threshold": "threshold",
  "bld.n.segments": "N_SEGMENTS",
  "bld.n.sensory": "SENSORY",
  "bld.n.words": "N_WORDS",
  "bld.n.decay": "decayLUT[64]",
  "bld.n.decayV": "0.98^(2^i) in Q16, written at deploy",
  "bld.n.gates": "gateTable[256]",
  "bld.n.gatesV": "one 64-bit gate per hash bucket of the input",
  "bld.needs.p2":
    "The semantics - sensory write-through at <code>(n%60)*3</code>, three gated segments per tick, per-record clamp, reset on fire, word-at-a-time decay - are documented in <code>tools/vbrain.py</code>, the vectorised twin the kernel is checked against.",
  "bld.specimen.h": "How a specimen is made",
  "bld.specimen.p":
    "The same machinery, one level down: the connectome, a thought, the name, the art and the registry row it all ends in.",
  "bld.replay.h": "Replay a tick locally",
  "bld.replay.p":
    "With the payloads on disk, the same brain runs on your machine. Feed the same inputs in order and compare: the per-tick counts (synapses, spiked) come straight off the chain's own events, and the state root is the value to check against the chain's stateRoot().",
  "bld.replay.p2":
    'The BrainState event carries counts; the fired ids are recomputed here, which is how a claim\\\'s "this thought fired this neuron" is checked without trusting anyone.',
  "bld.path1.k": "SHORTEST",
  "bld.path1.h": "Build a dapp on the brain",
  "bld.path1.p":
    "Read BrainState for thoughts, call feed() to make one, and let the registry hold the hatches. Nothing needs deploying: the addresses are in the README.",
  "bld.path2.k": "RESEARCH",
  "bld.path2.h": "Treat the tapes as a dataset",
  "bld.path2.p":
    "Eighty-five files, sha256 in the manifest, byte-comparable against the chain. Pull them into a notebook, an analysis, or another simulator.",
  "bld.path3.k": "LONGEST",
  "bld.path3.h": "Deploy your own brain",
  "bld.path3.p":
    "The kernel is the frozen LIF network and the tapes are just payloads: register a different connectome and you have a different brain on the same contract.",
  "bld.limits.h": "What this is",
  "bld.limits.l1":
    "The flybrain itself, onchain: the intact central nervous system of an adult female <i>Drosophila melanogaster</i> - the BANC v888 connectome, 169,088 cells - executing in Arc consensus as a deterministic integer leaky integrate-and-fire network. Connectivity, partners and synapse signs are <b>measured</b>; membrane dynamics are <b>specified in full</b>; and exactness is proven over the organism's entire emitted history - every thought it has ever had replays off-chain, spike for spike and hash for hash.",
  "bld.limits.l2":
    "The etched wiring is the two strongest synapses per source neuron - 325,292 records, 2.4% of the 13,620,865-edge reference connectome - and the backbone carries the whole: the full network's spike train reproduces at Jaccard 1.0000, 100/100 ticks bit-identical in <code>stateRoot</code>, and the integer kernel holds 0.9610 pooled spike Jaccard against a float64 LIF reference.",
  "bld.limits.l3":
    "The weights are a published recipe, not a fit: <code>sign x synapse count x 64</code>, one global scale, no training set and no optimizer. The deployed wiring is the dataset's topology.",
  "bld.limits.l4":
    "Every one of the 169,088 cells sits at a fixed address (ids <code>0..169,087</code>), and all 325,292 synaptic targets resolve inside the living population - measured across every etched record.",
  "bld.limits.l5":
    "Every number on this page is read from the chain or decoded from the repository's own bytes, and can be reproduced with the commands above. The anatomy is not described, it is fetched: <code>eth_getCode</code> returns the connectome slice byte for byte.",
  "bld.limits.l6":
    "The reduction to the etched backbone is an empirical question with a number attached, and it ships measured - while the BANC data stays the BANC project's: our code is AGPL-3.0, our derived artifacts CC BY-NC-SA 4.0, upstream terms are theirs - see <code>PROVENANCE.md</code>.",
  "bld.limits.l7":
    "Exactness measured over a life, not a sample: the census of every <code>BrainState</code> ever emitted - 74 thoughts, ticks 1-74 - replays through the off-chain twin at 74/74 spike lists, <code>stateRoot()</code> byte-isomorphic at four archive checkpoints including the living head. On the record: 590 spikes from 192 cells across the sixty-column sensory epithelium, suprathreshold summation of afferent volleys, and a stimulus-locked period-2 orbit - the field study ships its data and its audit commands.",
  "sci1.h3": "What BANC v888 is",
  "sci1.p1":
    "BANC v888 is the <b>sealed connectome</b> of the adult fly brain: 169,078 individually addressable neurons, frozen at version 888 and anchored onchain. Nothing can be added to it; nothing can be removed from it. It is the fixed substrate on which every thought in this app runs.",
  "sci1.p2":
    "Because the connectome is sealed, everything downstream is checkable. A neuron id, a tick, a fired set - each can be recomputed and verified by anyone, which is what makes a hatch a specimen rather than a picture.",
  "sci1.kNeurons": "Neurons",
  "sci1.vNeurons": "<b>169,078</b> source neurons · 169,088 slots",
  "sci1.kVersion": "Version",
  "sci1.vVersion": "v888 · <b>sealed</b>, immutable",
  "sci1.kSubstrate": "Substrate",
  "sci1.vSubstrate": "Arc chain 5042",
  "sci1.kHatches": "Hatches",
  "sci1.vHatches": "one per neuron, <b>ever</b>",
  "sci2.h3": "What a thought is",
  "sci2.p1":
    "A thought is one pass of the brain. It is not a message and not a prompt - it is a <b>measurable event</b> with a tick number, a state root, and a fired set:",
  "sci2.pk1": "01 · Feed",
  "sci2.pt1": "Feed accepted",
  "sci2.pd1": "25 OBRAIN burned · one feed opens one thought",
  "sci2.pk2": "02 · Think",
  "sci2.pt2": "Brain advances one tick",
  "sci2.pd2": "BANC v888 computes the next state",
  "sci2.pk3": "03 · Event",
  "sci2.pt3": "BrainState emitted",
  "sci2.pd3": "state root + fired set, onchain",
  "sci2.pk4": "04 · Fired",
  "sci2.pt4": "Neuron ids, addressable",
  "sci2.pd4": "each one hatchable exactly once",
  "sci2.p2":
    "Only neurons that appear in a fired set can ever be hatched. The fired set is public; the pick list on the Hatch page is simply tonight's slice of it.",
  "sci3.h3": "How the Latin name is derived",
  "sci3.p1":
    "Each fired neuron receives a binomial in the museum's register. Nothing in the name is invented at random - both words are <b>read off the anatomy and the event</b>:",
  "sci3.p2":
    "<b>The genus</b> comes from the neuron's region and cell class - the anatomical stem, as in a field guide: the optic lobe yields <i>Optica</i>, the antennal lobe yields <i>Antennalis</i>, the central complex yields <i>Centralis</i>.",
  "sci3.p3":
    "<b>The epithet</b> is two facts in one word. The middle term records the side - <i>sinistra</i>, <i>dextra</i>, <i>dorsalis</i> - and the final epithet is selected deterministically from a fixed Latin lexicon by the thought's state root. The same root always yields the same word; two neurons sharing a root share a species name, exactly as two specimens of one species share a description.",
  "sci3.cap1": "Region / cell class → genus",
  "sci3.thRegion": "Region",
  "sci3.thGenus": "Genus",
  "sci3.r1": "Optica lobe",
  "sci3.r2": "Medulla",
  "sci3.r3": "Lobula",
  "sci3.r4": "Antennal lobe",
  "sci3.r5": "Central complex",
  "sci3.r6": "Ventral cord",
  "sci3.r7": "Calyx",
  "sci3.cap2": "State root → epithet (excerpt)",
  "sci3.thRoot": "State root",
  "sci3.thEpithet": "Epithet",
  "sci3.example":
    "Worked example: neuron <b>N-47823</b>, optic lobe, left hemisphere, thought root <b>0x0a…</b> → <i>Optica sinistra affinis</i>.",
  "sci4.h3": "How the art is made",
  "sci4.p1":
    'Every specimen carries a <b>genome</b>: its neuron id. The plate art is <b>deterministic</b> - drawn by a seeded renderer (<code class="mono" style="font-size: 12px">svg[data-seed]</code>) that takes the genome as its only input. The same genome always draws the same dots, arcs and spokes, on every machine, at any time, forever.',
  "sci4.p2":
    "The art is not generated per view and not chosen by taste. It is derived, the way a specimen's morphology is derived from its genes. Two browsers showing the same neuron are showing the same animal.",
  "sci5.h3": "What the NeuronRegistry records",
  "sci5.p1":
    "The registry records one unverified claim per neuron (neuronId -> thoughtTxHash -> token). Two things are enforced onchain; each claim is verified offchain:",
  "sci5.p2":
    "<b>One hatch per neuron, ever.</b> A fired neuron may be hatched exactly once, for life. The registry rejects any second attempt, from anyone, including you.",
  "sci5.p3":
    "<b>Supply fixed at one.</b> A specimen token mints a single unit, to the hatcher, and can never mint again.",
  "sci5.p4":
    "<b>Registry numbers in hatch order.</b> A number records an unverified claim - the registry itself does not prove a token genuine. A card's claim verifies offchain: the row's thought tx receipt must fire that neuron, and the card contract must hold the specimen.",
  "sci6.h3": "Contract addresses",
  "sci6.kToken": "OBRAIN token",
  "sci6.vToken": "<b>0x28f986a6…7f01</b> · burned on every feed",
  "sci6.kLaunchpad": "Specimen Card",
  "sci6.vLaunchpad": "<b>0x3f54…35ae</b> · one card per neuron, forever",
  "sci6.kRegistry": "Specimen Registry",
  "sci6.vRegistry": "<b>0x72cdc3C2…a944</b> · card claim index",
  "sci6.kUsdc": "USDC (Arc)",
  "sci6.vUsdc": "<b>0x3600…0000</b> · gas is paid in native USDC",
  "sci6.kNetwork": "Network",
  "sci6.vNetwork": "<b>Arc mainnet · chain 5042</b>",
  "side.left": "Left",
  "side.right": "Right",
  "side.dorsal": "Dorsal",
  "cell.fallback": "Intrinsic",
  "plate.aria": "Deterministic plate for genome {seed}",
  "plate.cap": "GENOME {seed} · DETERMINISTIC PLATE",
  "plate.profile": "Profile",
  "plate.profileLoading": "Reading the biographer's profile…",
  "plate.provenance": "Provenance",
  "plate.provenanceText":
    "{id} · fired at tick T.{tick} in thought tx {tx} · genome {seed} · BANC v888 · Arc chain 5042. Verified against the fired set of the BrainState event.",
  "plate.terms": "Hatch terms",
  "plate.termsText":
    "Supply fixed at one. One hatch per neuron, ever, for life. Gas only - the card contract charges nothing. Registry number assigned in hatch order; the claim is verified offchain against the fired set of the thought.",
  "profile.temperament": "Temperament",
  "profile.habitat": "Habitat fit",
  "profile.light": "Light-averse",
  "profile.source": "SOURCE · {src}",
  "prep.specimen": "Specimen",
  "prep.neuron": "Neuron",
  "prep.card": "Card",
  "prep.cardValue": "<b>{addr}</b> · one card per neuron, ever",
  "prep.cost": "Cost",
  "prep.costValue": "<b>gas only</b> · no launch fee",
  "prep.claim": "Claim",
  "prep.claimValue": "{addr} · the registry row the collection reads",
  "prep.recipient": "Recipient",
  "prep.recipientValue": "{addr} · the thought's feeder (D5)",
  "prep.networkValue": "Arc mainnet · chain 5042",
  "launch.cap": "GENOME {seed} · CARD PLATE",
  "card.claim": "registry claim · verify offchain",
  "stage.live": "LIVE · TICK {n}",
  "home.sub": "Drosophila melanogaster · the first flybrain onchain",
  "home.neurons": "Neurons",
  "bd.cellTypes": "Cell types",
  "tl.aria": "Neuro telemetry",
  "tl.readout": "Readout · p0–p3",
  "tl.readoutS": "against the threshold {n}",
  "tl.raster": "Spike raster",
  "tl.rasterS": "{n} thoughts · {c} cells fired",
  "tl.rasterFoot": "x = thought · y = sensory cell 0–255",
  "tl.senses": "Sensory epithelium",
  "tl.sensesS": "{n} feeds across 60 channels",
  "tl.lastFeed": "Last feed",
  "tl.channel": "channel {n}",
  "tl.heart": "Arc heartbeat",
  "tl.heartS": "every block as it lands",
  "tl.block": "Block",
  "tl.txs": "Txs",
  "tl.rate": "Blocks / min",
  "tl.neuron": "Neuron dossier",
  "tl.neuronS": "the newest thought's fired cells",
  "tl.rootId": "Root id",
  "tl.type": "Cell type",
  "tl.class": "Class",
  "tl.super": "Super class",
  "tl.where": "Region · side",
  "tl.degree": "Out · in",
  "tl.census": "Cell census",
  "tl.censusS": "largest BANC classes · lit when a thought fires one",
  "tl.sign": "Synapse sign",
  "tl.signS": "the etched weights, excitatory against inhibitory",
  "tl.exc": "Excitatory",
  "tl.inh": "Inhibitory",
  "tl.mag": "Mean |w|",
  "tl.scope": "Firing oscilloscope",
  "tl.spikes": "spikes",
  "tl.synapses": "synapses",
  "tl.perThought": "per thought",
  "tl.mean": "Mean spikes",
  "tl.peak": "Peak",
  "tl.ratio": "Syn / spike",
  "bd.feedTag": "feed · channel {n}",
  "bd.strip": "BRAIN {addr} · D. MELANOGASTER · BLOCK {block}",
  "bd.step1": "Connect",
  "bd.step2": "Prune",
  "bd.step3": "Quantize",
  "bd.step4": "Etch",
  "bd.step5": "Seal",
  "bd.step6": "Think",
  "bd.stagesK": "Six stages · how a fly brain got onchain, one step at a time",
  "bd.stageChip": "STAGE {n} / 06",
  "bd.st1": "Connectome",
  "bd.st2": "Pruner",
  "bd.st3": "Quantizer",
  "bd.st4": "Etcher",
  "bd.st5": "Sealer",
  "bd.st6": "Thinker",
  "bd.run": "Run",
  "bd.v1": "{n} cells",
  "bd.v2": "{n} links",
  "bd.v3": "{n} B / link",
  "bd.v4": "{n} tapes",
  "bd.v5": "θ {n}",
  "bd.v6": "tick {n}",
  "bd.waiting": "waiting",
  "bd.done": "/ done",
  "bd.r1": "› reading BANC v888",
  "bd.r2": "› keeping the top two",
  "bd.r3": "› packing 5-byte records",
  "bd.r4": "› etching the tapes",
  "bd.r5": "› sealing the wiring",
  "bd.r6": "› thinking on Arc",
  "bd.modelK": "The fly model · one fruit fly, <mark>every neuron</mark> · live",
  "bd.locked": "Locked",
  "bd.zooming": "Zooming",
  "bd.closeUp": "Close-up",
  "bd.stop": "STOP {n} / 6",
  "bd.w.specimen": "Full specimen · orbit",
  "bd.ws.specimen": "D. melanogaster brain · BANC v888 · glass cuticle",
  "bd.w.eyes": "Compound eyes · surface",
  "bd.ws.eyes": "01 connect · the senses come in",
  "bd.w.cuticle": "Through the cuticle · glass",
  "bd.ws.cuticle": "02 prune · the body made clear",
  "bd.w.brain": "Inside the fly · connectome",
  "bd.ws.brain": "03 quantize · every cell where BANC put it",
  "bd.w.optic": "Optic lobe · vision",
  "bd.ws.optic": "04 etch · the wiring in 85 tapes",
  "bd.w.memory": "Mushroom body · memory",
  "bd.ws.memory": "05 seal · the wiring can no longer change",
  "bd.w.map": "Follow back · full fly",
  "bd.ws.map": "06 think · every feed, a thought on Arc",
  "bd.sn.specimen": "Full specimen",
  "bd.sn.eyes": "Compound eyes",
  "bd.sn.cuticle": "Cuticle",
  "bd.sn.brain": "Whole brain",
  "bd.sn.optic": "Optic lobe",
  "bd.sn.memory": "Mushroom body",
  "bd.sn.map": "Full specimen",
  "bd.c.eye": "Compound eye",
  "bd.c.eyeS": "{n} photoreceptors · BANC",
  "bd.c.antenna": "antenna · smell",
  "bd.c.brain": "Central brain",
  "bd.c.brainS": "{n} neurons · {s} synapses",
  "bd.c.optic": "Optic lobe",
  "bd.c.opticS": "{n} cells · motion · colour",
  "bd.c.memory": "Mushroom body",
  "bd.c.memoryS": "{n} Kenyon cells · memory",
  "bd.bannerK": "One fruit fly · one mind",
  "bd.bannerT": "{n} neurons.",
  "bd.bannerS": "Every one of them, onchain.",
  "bd.axis": "Depth",
  "bd.ax.surface": "Surface",
  "bd.ax.eyes": "Eyes",
  "bd.ax.cuticle": "Cuticle",
  "bd.ax.brain": "Brain",
  "bd.ax.optic": "Optic",
  "bd.ax.memory": "Memory",
  "bd.ax.map": "Map",
  "bd.ax.deeper": "Deeper",
  "bd.m1": "Spiked · synapses",
  "bd.m2": "State",
  "bd.m3": "Transparency",
  "bd.m4": "Classes · cell types",
  "bd.m5": "Parallax",
  "bd.m6": "Block",
  "bd.actK": "Activity · per region",
  "bd.actS": "{n} thoughts",
  "bd.actChip": "LIVE · TICK {n}",
  "bd.g1": "Optic lobe",
  "bd.g2": "Antennal lobe",
  "bd.g3": "Mushroom body",
  "bd.g4": "Central cplx",
  "bd.g5": "Lateral horn",
  "bd.g6": "Desc · VNC",
  "bd.actFrom": "tick {n}",
  "bd.now": "now",
  "bd.actFoot": "Row = region · spike = cells fired in a thought",
  "bd.conK": "Contact map · which regions talk",
  "bd.conS": "{n} synapses",
  "bd.pair": "PAIR {p}",
  "bd.hot": "Hot pair",
  "bd.hotS": "{n} synapses",
  "bd.conFoot": "Cell = synapses between two regions · read from the 85 tapes",
  "bd.rec": "Rec",
  "bd.obsS": "Live fly · 3D scope",
  "bd.watching": "WATCHING · {s}",
  "bd.turn": "TURN {n}° · TICK {t}",
  "bd.footL": "One fly, one <b>brain</b> · every neuron onchain, every thought a block",
  "bd.footR1": "OBRAIN · a connectome onchain",
  "bd.footR2": "Rendered specimen · brain data read from Arc",
  "home.synapses": "Synapses",
  "home.obs": "Under observation",
  "tape.tick":
    "TICK <b>{tick}</b> · THOUGHT <b>#{thoughts}</b> · <b>{fired}</b> FIRED · <b>{burned}</b> OBRAIN BURNED",
  "tape.fired": "TICK <b>{tick}</b> · <b>{fired}</b> NEURONS FIRED",
  "tape.unreachable": "INDEXER UNREACHABLE · THE TAPE IS QUIET",
  "wallet.connected": "Connected · click to disconnect",
  "wallet.missing": "No wallet found in this browser",
};

export type TKey = keyof typeof en;

type Dict = Record<TKey, string>;

// ---- the Simplified Chinese copy -------------------------------------------
// Latin binomials, region genus stems (Optica, Cerebralis, ...), chain terms
// (Arc, BANC v888, OBRAIN) and contract identifiers stay Latin: they are
// specimen taxonomy and onchain nouns, not UI copy.

export const zh: Dict = {
  title: "OBRAIN · 首个链上果蝇脑",
  skip: "跳到正文",
  "nav.aria": "主导航",
  "nav.brain": "脑",
  "docs.index": "文档",
  "docs.onThisPage": "本页内容",
  "docs.prev": "上一页",
  "docs.next": "下一页",
  "docs.kicker": "文档",
  "docs.title": "阅读事实来源",
  "docs.aria": "文档",
  "nav.docs": "文档",
  "nav.flybookAria": "Flybook 果蝇殖民地 - 在新标签页打开",
  "lang.aria": "语言",
  "wallet.connect": "连接钱包",
  "wallet.title": "连接 Arc 5042 钱包",
  "brain.aria": "脑 - 实时视图",
  "brain.tick": "刻",
  "stage.figNote": "图 1 · 思考中的大脑 · 背侧投影 · BANC v888",
  "stage.legend":
    "标本：成年雌性<i>黑腹果蝇</i>的完整中枢神经系统，运行于 Arc 共识 - 169,088 个细胞，325,292 条突触记录以字节码刻蚀。此舞台上的每一次闪光都是一个已挖出的 <code>BrainState</code> 事件；生物体全部的发射史在链下以 74/74 脉冲列表重放，<code>stateRoot()</code> 逐字节同构。田野记录 CS-001：192 个细胞的 590 次脉冲，尽在六十柱感觉上皮之内。",
  "tape.label": "思想记录带",
  "tape.demo1": "刻 <b>4,087</b> · 思想 <b>#311</b> · <b>141</b> 点火 · 小叶 · 左",
  "tape.demo2": "喂养已接受 · 刻 <b>4,086</b> · <b>203</b> 个神经元点火 · 髓叶 · 右",
  "tape.demo3": "刻 <b>4,085</b> · <b>96</b> 点火 · 燃烧 25 OBRAIN",
  "vit.aria": "大脑状态，直接读自链上",
  "vit.kicker": "状态",
  "vit.title": "大脑此刻的样子",
  "vit.age": "年龄 · 已有的思考数",
  "vit.ageSub": "每次喂食打开一次思考，一次思考一刻",
  "vit.lastThought": "最近一次思考",
  "vit.spiked": "个神经元点火",
  "vit.synapses": "个突触",
  "vit.spikedSub": "全脑 {total} · 比上一次思考 {delta}",
  "vit.spikedSubFirst": "共 {total} · 这是窗口里最新的思考",
  "vit.spikedSubAlone": "这是窗口里最新的思考",
  "vit.spikedSubAloneBefore": "比上一次思考 {delta}",
  "vit.synapsesSub": "比上一次思考 {delta}",
  "vit.synapsesSubFirst": "那一次思考跨越的信号数",
  "vit.field": "电场 · 膜电位",
  "vit.chargedSub": "个细胞带电，共 {total} · 上次读取后新增 {gained}",
  "vit.peak": "峰值电荷",
  "vit.above": "到达触发线",
  "vit.peakSub": "最接近点火的那个细胞 · 内核在 {trigger} 点火",
  "vit.fieldWaiting": "正在从节点读取电场…",
  "vit.sense": "感觉 · 最近一次触碰",
  "vit.senseChannel": "通道",
  "vit.senseDepth": "电荷 /255",
  "vit.senseNone": "窗口里还没有喂食",
  "vit.senseLight": "对单个通道轻轻一触",
  "vit.senseMost": "已接近阈值",
  "vit.senseFires": "该通道立即点火",
  "vit.root": "状态根",
  "vit.rootSub": "每次思考写下的指纹，刻 {tick}",
  "vit.wiring": "布线 · BANC v888",
  "vit.wiringValue": "{cells} 个细胞 · {tapes} 段磁带",
  "vit.sealSealed": "已封存 · 布线不再改变",
  "vit.sealOpen": "开放 · 仍可发布磁带",
  "vit.noteMine": "你的喂食 · 大脑在刻 {tick} 思考 · {fired} 个神经元点火",
  "vit.notePoke": "你的喂食 · 通道 {c} 充入 {d}/255 · 思考正在进行",
  "vit.noteThought": "大脑在刻 {tick} 思考 · {fired} 个神经元点火",
  "vit.reading": "正在从链上读取大脑…",
  "vit.tabsAria": "大脑状态",
  "vit.tabState": "状态",
  "vit.tabLog": "日志",
  "vit.logHint": "点开一次思考，读它留下的 BrainState 事件",
  "vit.logEmpty": "窗口里还没有思考",
  "vit.logTick": "刻 {tick}",
  "vit.logMeta": "点火 {fired} · 突触 {synapses}",
  "vit.logRowLabel": "刻 {tick} 的思考：{fired} 个神经元点火，{synapses} 个突触被跨越",
  "vit.dTick": "被索引的刻",
  "vit.dReadout": "读数 · 神经元 0-3",
  "vit.dFired": "点火的神经元",
  "vit.dSpiked": "内核报告的脉冲数",
  "vit.dSpikedOf": "个神经元点火",
  "vit.dSynapses": "跨越的突触",
  "vit.dCapped": "脉冲上限",
  "vit.dCappedYes": "达到 · 该刻被截断",
  "vit.dCappedNo": "未达到",
  "vit.dPoke": "感觉触碰",
  "vit.dPokeNone": "无 · 内核没有被交给它",
  "vit.dPokeValue": "通道 {c} · 电荷 {d}/255",
  "vit.dBlock": "区块",
  "vit.dTx": "交易 {tx} ↗",
  "vit.quiet": "窗口里还没有思考 - 大脑只在被喂食时思考",
  "feed.aria": "喂养大脑 - 用 OBRAIN 支付费用",
  "feed.kicker": "喂养",
  "feed.title": "喂养大脑",
  "feed.channel": "感觉通道",
  "feed.tier": "投喂档位",
  "feed.burn": "投喂",
  "feed.balance": "余额",
  "feed.approve": "授权 OBRAIN",
  "feed.ready": "喂养大脑",
  "feed.buy": "购买 OBRAIN",
  "feed.buyAria": "购买 OBRAIN - 新标签页打开",
  "feed.log": "喂养记录",
  "feed.logRow": "{n} OBRAIN · 通道 {c}",
  "feed.logEmpty": "还没有喂养 - 第一笔费用会开启第一个思想。",
  "feed.insufficient": "还差 {n} OBRAIN - 在市场上购买",
  "feed.grant": "一次授权即可覆盖之后的每一次喂养：大脑只能把 OBRAIN 发送到死地址。",
  "feed.confirm": "请在钱包中确认…",
  "feed.approving": "正在授权 OBRAIN…",
  "feed.feeding": "正在支付 {n} OBRAIN…",
  "feed.pending": "费用已支付 · 等待思想…",
  "feed.landed": "费用已支付 · 今夜索引滞后 - 记录带会显示这次思想",
  "feed.reading": "正在读取你的 OBRAIN…",
  "feed.readChain": "正在读取大脑自身的价格…",
  "feed.readFailed": "无法读取你的 OBRAIN 余额：{reason}",
  "feed.noWallet": "连接右上角的钱包即可支付费用。",
  "feed.readyNote": "从你的钱包支付 {n} OBRAIN - 大脑在同一笔交易中思考。",
  "feed.thought": "思想已接受 · 刻 <b>{tick}</b> · <b>{fired}</b> 个神经元点火",
  "feed.thoughtUndecoded": "费用已接受 · 刻 <b>{tick}</b> · 等待点火集合",
  "feed.failed": "喂养失败：{reason}",
  "feed.unknownError": "钱包未说明原因地拒绝了",
  "feed.charge0": "充电 31/255 · 对单个通道轻轻一触",
  "feed.charge1": "充电 127/255 · 已接近阈值",
  "feed.charge2": "充电 255/255 · 该通道立即点火",
  "hatch.aria": "孵化 - 启动台",
  "hatch.kicker": "启动台",
  "hatch.title": "孵化一件标本",
  "hatch.sub": "三步 · 仅在最后签名",
  "hatch.intro1": "今夜大脑已点火",
  "hatch.intro2":
    "个神经元。每个点火的神经元终生只可孵化一次。请从容审阅；签名只发生在你自己的钱包中。",
  "hatch.s1t": "挑选点火的神经元",
  "hatch.s1s": "来自今夜的窗口",
  "hatch.s2t": "审阅标本",
  "hatch.s2s": "图版与来源",
  "hatch.s3t": "铸造卡片",
  "hatch.s3s": "一次按下 · 两次签名",
  "hatch.step1Sub": "今夜的窗口 · 最新在前 · 每个神经元终生只孵化一次",
  "hatch.firedCount": "已显示 {n} 枚神经元",
  "th.art": "图版",
  "th.id": "编号",
  "th.binom": "拉丁学名",
  "th.region": "区域 · 侧 · 细胞",
  "th.pick": "选择",
  "pick.done": "已选",
  "hatch.step1Hint": "选择一行以继续 · 尚未预留任何内容",
  "hatch.step1Next": "继续至审阅",
  "hatch.step2Sub": "由基因组构成的图版 · 读取一次，永久保存",
  "hatch.back": "返回",
  "hatch.step2Next": "继续至孵化",
  "launch.sub": "一个神经元 · 一张卡 · 永久",
  "launch.chipCard": "标本卡",
  "launch.cardK": "你会得到",
  "launch.cardV": "<b>一张卡</b>，永久",
  "launch.cardS": "卡片把标本的生物学写在链上：所属脑区、细胞类别，以及它那次思想做了什么。",
  "launch.pendCard": "按下后铸造",
  "launch.costS": "只需 gas。卡合约不收费。",
  "launch.claimS": "馆藏读取的那一行，紧随铸造之后写入。",
  "launch.whoK": "谁可签名",
  "launch.whoV": "<b>{addr}</b> - 该念头的喂养钱包（D5）。",
  "launch.whoS": "只有喂养该思想的钱包可以铸造这张卡，他人不可。",
  "launch.after": "铸造之后",
  "launch.viewTokenAria": "在 Arc 浏览器上的标本卡",
  "launch.viewTxAria": "Arc 浏览器上的铸造交易",
  "launch.bandOpenedK": "可以铸造",
  "launch.bandOpenedT":
    "图版全部由基因组读出：神经元、卡合约、费用与声明。按下才签名 - 本页不会自行发送。",
  "launch.noteOpened": "图版没有缺项。按下即铸造卡片；本页不签名。",
  "launch.noteRefused": "按键仍然有效。被拒不会产生任何代价，可以重复尝试。",
  "launch.bandWalletK": "钱包拒绝",
  "launch.bandWalletS": "未签名",
  "launch.bandNoWalletK": "未连接钱包",
  "launch.bandNoWalletT":
    "孵化费由喂出该念头的钱包支付，因此需要一个钱包。没有钱包，图版本身依然完整。",
  "launch.bandWrongWalletK": "非喂养钱包",
  "launch.bandWrongWalletT": "浏览器中的钱包不是喂出该念头的钱包，无法为其付费或签名。",
  "launch.bandSigningK": "请在钱包中确认",
  "launch.bandSigningT": "你的钱包正在请求批准。本页会等待，不会自行发送任何东西。",
  "launch.noteSigning": "确认之前，请逐行核对负载。",
  "launch.bandLaunchedK": "卡片已铸造 · 待声明",
  "launch.bandLaunchedT": "代币已存在，领取仍未签名。签署后标本才会写入注册表。",
  "launch.noteLaunched": "两次签名，两步：先铸造卡片，再写入声明。",
  "launch.bandClaimedK": "卡片已铸造",
  "launch.bandClaimedT": "{binom} · {id} - 一个神经元一张卡，永久。",
  "launch.noteClaimed": "一个神经元一张卡，永久 - 注册表按顺序编号。",
  "launch.bandUnconfirmedK": "铸造未确认",
  "launch.bandUnconfirmedS": "未领取",
  "launch.go": "铸造卡片",
  "launch.confirm": "请在钱包中确认…",
  "launch.claim": "签名注册",
  "launch.claiming": "请在钱包中确认注册…",
  "launch.claimNote": "卡片已铸造 · 再签一次即入馆藏。",
  "launch.claimFailed": "卡片已铸造 · 声明失败：{reason}",
  "launch.claimed": "卡片已铸造",
  "launch.viewToken": "卡片",
  "launch.viewTx": "交易",
  "launch.unconfirmed": "{addr} 的卡片尚未确认 · 交易 {tx}",
  "launch.failed": "钱包拒绝：{reason}",
  "launch.noWallet": "连接钱包即可铸造卡片。",
  "launch.notCreator": "只有喂养钱包可以铸造这张卡：{addr}",
  "launch.feederUnknown": "该思想没有喂养钱包，只有喂养者才能孵化（D5）。",
  "hlist.aria": "已孵化标本",
  "hlist.kicker": "已铸造名录",
  "hlist.title": "已孵化名册",
  "hlist.sub": "一个神经元一张卡，永久。每一行都是从 Arc 读出的注册表声明。",
  "hlist.count": "已孵化 <b>{n}</b> 件",
  "hlist.token": "卡片",
  "hlist.market": "链上",
  "hlist.open": "在链上打开",
  "hlist.openAria": "在 Arc 浏览器打开 {name} - 新标签页",
  "hlist.empty": "尚无标本孵化。喂养大脑，选择一个点火神经元，然后从容制备。",
  "col.aria": "永久馆藏",
  "col.kicker": "永久馆藏",
  "col.title": "已孵化的标本",
  "col.sub": "每张卡片都携带注册表声明，并经链下核验",
  "col.reading": "正在读取注册表…",
  "col.empty":
    "注册表读取完毕：暂无声明。馆藏始于你的第一次孵化。喂养大脑，挑一枚点火的神经元，从容备妥。",
  "facts.seed": "种子",
  "facts.fired": "点火集合",
  "facts.spiked": "点火",
  "facts.synapses": "突触传递",
  "facts.type": "细胞类型",
  "facts.class": "细胞类别",
  "facts.flow": "流向",
  "facts.side": "侧",
  "facts.region": "脑区",
  "hatch.gridAria": "已点火的神经元卡片",
  "filter.legacy": "元老",
  "filter.rare": "稀有",
  "filter.uncommon": "少见",
  "filter.common": "普通",
  "filter.all": "全部等级",
  "filter.result": "共 {total} 张，显示 <b>{shown}</b> 张",
  "filter.emptyRegion": "此脑区 {region} 没有符合所选层级的标本。",
  "filter.emptyTier": "此层级 {tier} 还没有标本 - 筛选是诚实的，架子是空的。",
  "filter.clearFilters": "清除筛选",
  "filter.empty": "没有符合此筛选的标本",
  "filter.allRegions": "全部脑区",
  "filter.region": "脑区",
  "filter.tierLevel": "层级",
  "filter.clear": "清除",
  "filter.title": "筛选",
  "col.buy": "卡片 ↗",
  "col.countAll": "已孵化 <b>{hatched}</b> 件 · 共 <b>{total}</b> 枚可能",
  "col.countRegion":
    "{region} 中 <b>{shown}</b> 件 · 已孵化 <b>{hatched}</b> 件 · 共 <b>{total}</b> 枚可能",
  "bld.aria": "基于链上脑图谱构建 - 链上的连接组",
  "bld.kicker": "构建",
  "bld.title": "继承这 85 条磁带",
  "bld.sub": "大脑的突触就在链上，是任何人都能读取的合约代码",
  "bld.countLabel": "TAPES_COUNT()",
  "bld.what.h": "一条磁带是什么",
  "bld.what.p1":
    "一个 <b>ImmortalFruitFliesTape</b> 合约保存连接组的一个片段：其构造函数把突触载荷作为运行时代码返回，因此 <b>eth_getCode(tape) 就是数据本身</b>。85 条磁带承载整个脉冲网络，登记在大脑自己的索引里 - 没有服务器可问，只有一条可读的链。",
  "bld.what.p2":
    "它们不依赖这个网站。只要 Arc 还保存状态，任何人 - 脚本、表格、模型，或你自己部署的内核 - 都能读取这份连接组。",
  "bld.index": "本页内容",
  "bld.kv.brain": "大脑",
  "bld.kv.payload": "载荷",
  "bld.kv.payloadV": "tapes(i) 处的运行时代码，逐字节一致",
  "bld.kv.slots": "槽位 · 源神经元",
  "bld.kv.slotsV": "169,088 个槽位 · 其中 169,078 个有出向突触",
  "bld.kv.sparse": "密度",
  "bld.kv.sparseV": "每对取最强的 top-2 突触，来自 BANC v888",
  "bld.manifest.h": "85 条，按链上的原样",
  "bld.manifest.dl": "下载清单",
  "bld.manifest.reading": "正在从索引读取磁带…",
  "bld.manifest.count": "{n} 条磁带 · 大脑 {brain} · 只遍历一次，由索引提供",
  "bld.manifest.empty": "索引里还没有磁带：索引器启动时会从链上遍历一次",
  "bld.th.index": "#",
  "bld.th.address": "磁带合约",
  "bld.th.start": "起始神经元",
  "bld.th.bytes": "字节",
  "bld.th.sha": "sha256",
  "bld.decode.label": "在浏览器里解码一条磁带",
  "bld.decode.go": "解码",
  "bld.decode.out":
    "磁带 {index}：{neurons} 个源神经元 · {synapses} 个突触 · {targets} 个不同目标 · 权重 {lightest}…{heaviest}",
  "bld.decode.error": "无法解码磁带 {index}：{reason}",
  "bld.layout.h": "一条磁带里有什么",
  "bld.layout.p":
    "全部大端序：先是 16 位偏移表，然后每个突触 5 字节。第一个 uint16 同时充当头部，因为它也是第一个偏移量。",
  "bld.layout.p2":
    "<code>tools/tapes.py</code> 写出这个布局，<code>web/src/tapes.ts</code> 解码它 - 同一套规则，一个在 Python，一个在浏览器，上面的按钮跑的就是它。",
  "bld.needs.h": "忠实复现还需要什么",
  "bld.needs.p":
    "磁带是连接组，不是大脑状态。下面这些来自同一个合约，每一个都会改变一步计算的结果：",
  "bld.n.threshold": "threshold",
  "bld.n.segments": "N_SEGMENTS",
  "bld.n.sensory": "SENSORY",
  "bld.n.words": "N_WORDS",
  "bld.n.decay": "decayLUT[64]",
  "bld.n.decayV": "0.98^(2^i) 的 Q16 表示，部署时写入",
  "bld.n.gates": "gateTable[256]",
  "bld.n.gatesV": "输入哈希桶对应一个 64 位门",
  "bld.needs.p2":
    "语义 - 感觉通道按 <code>(n%60)*3</code> 写穿、每步三个带门分段、逐记录钳位、触发后复位、按字衰减 - 记录在 <code>tools/vbrain.py</code>，即用来校验链上内核的向量化孪生实现。",
  "bld.specimen.h": "标本是如何制成的",
  "bld.specimen.p":
    "同一套机制，再往下一层：连接组、一次思考、名字、图像，以及最终落在注册表里的那一行。",
  "bld.replay.h": "在本地重放一步",
  "bld.replay.p":
    "把载荷写到磁盘后，同一个大脑就能在你的机器上运行。按顺序喂入相同的输入再比对：每步的计数（synapses、spiked）直接来自链上事件，而状态根要拿去和链上 stateRoot() 对照。",
  "bld.replay.p2":
    "BrainState 事件只带计数；触发神经元 ID 在这里重新计算，这正是“这次思考触发了这个神经元”可以不信任任何人的校验方式。",
  "bld.path1.k": "最短路径",
  "bld.path1.h": "在大脑上做一个 dapp",
  "bld.path1.p":
    "读 BrainState 得到思考，调 feed() 产生一次，孵化交给注册表。无需部署任何东西：地址都在 README 里。",
  "bld.path2.k": "研究",
  "bld.path2.h": "把磁带当作数据集",
  "bld.path2.p":
    "85 个文件，清单里有 sha256，可与链逐字节比对。放进 notebook、分析流程或另一个模拟器都可以。",
  "bld.path3.k": "最长路径",
  "bld.path3.h": "部署你自己的大脑",
  "bld.path3.p":
    "内核就是那套冻结的 LIF 网络，磁带只是载荷：换一份连接组，同一个合约上就是另一个大脑。",
  "bld.limits.h": "这是什么",
  "bld.limits.l1":
    "链上即蝇脑本身：成年雌性<i>黑腹果蝇</i>的完整中枢神经系统 - BANC v888 连接组，169,088 个细胞 - 以确定性整数泄漏积分-发放网络运行于 Arc 共识之中。连接、伙伴与突触符号皆为<b>实测</b>；膜动力学<b>完整规定</b>；而其精确性经生物体的全部发射史证明 - 它有生以来的每一个思维，皆可在链下逐脉冲、逐哈希地重放。",
  "bld.limits.l2":
    "刻蚀的布线是每个源神经元最强的两条突触 - 325,292 条记录，13,620,865 边参考连接组的 2.4% - 而这副骨架承载全部：完整网络的脉冲列车以 Jaccard 1.0000 复现，100/100 个 tick 的 <code>stateRoot</code> 逐位相同，整数内核对 float64 LIF 参考保持 0.9610 合并脉冲 Jaccard。",
  "bld.limits.l3":
    "权重是公开的配方，不是拟合：<code>符号 x 突触数 x 64</code>，只有一个全局尺度，没有训练集，也没有优化器。链上的布线就是数据集本身的拓扑。",
  "bld.limits.l4":
    "169,088 个细胞各居一个固定位址（ids <code>0..169,087</code>），全部 325,292 个突触目标都解析于存活的种群之内 - 于每一条刻蚀记录实测。",
  "bld.limits.l5":
    "本页每个数字都取自链上，或由仓库自身的字节解码得到，可用上面的命令复现。解剖不是被描述的，而是被取回的：<code>eth_getCode</code> 逐字节返回连接组切片。",
  "bld.limits.l6":
    "骨架降采样是一个带数字的实证问题，并且是带着测量结果发布的；同时 BANC 数据始终属于 BANC 项目：我们的代码是 AGPL-3.0，衍生工件是 CC BY-NC-SA 4.0，上游条款归上游 - 见 <code>PROVENANCE.md</code>。",
  "bld.limits.l7":
    "精确性以一生度量，而非抽样：对生物体从未间断发射的每一个 <code>BrainState</code> 的普查 - 74 个思维，tick 1-74 - 经链下孪生以 74/74 的脉冲列表重放，<code>stateRoot()</code> 在四个档案检查点（含活动头块）逐字节同构。记录在案：六十柱感觉上皮之上、192 个细胞的 590 次脉冲，传入脉冲群的阈上总和，以及一个刺激锁定的周期二轨道 - 田野研究随附其数据与审计命令。",
  "sci1.h3": "BANC v888 是什么",
  "sci1.p1":
    "BANC v888 是成年果蝇大脑的<b>封存连接组</b>：169,078 个可单独寻址的神经元，冻结于版本 888，锚定在链上。它不容增，不容删。它是本应用中每一次思想所运行于其上的固定基底。",
  "sci1.p2":
    "因为连接组已封存，下游的一切皆可查证。神经元编号、刻、点火集合 - 每一项都可由任何人重算并验证，这正是孵化之为标本、而非图画的原因。",
  "sci1.kNeurons": "神经元",
  "sci1.vNeurons": "<b>169,078</b> 个源神经元 · 169,088 个槽位",
  "sci1.kVersion": "版本",
  "sci1.vVersion": "v888 · <b>已封存</b>，不可更改",
  "sci1.kSubstrate": "基底",
  "sci1.vSubstrate": "Arc 链 5042",
  "sci1.kHatches": "孵化",
  "sci1.vHatches": "每个神经元一次，<b>终生</b>",
  "sci2.h3": "思想是什么",
  "sci2.p1":
    "思想是大脑的一次运转。它不是消息，也不是提示 - 它是一个<b>可测量的事件</b>，带有刻数、状态根与点火集合：",
  "sci2.pk1": "01 · 喂养",
  "sci2.pt1": "喂养已接受",
  "sci2.pd1": "燃烧 25 OBRAIN · 一次喂养开启一次思想",
  "sci2.pk2": "02 · 思考",
  "sci2.pt2": "大脑推进一刻",
  "sci2.pd2": "BANC v888 计算出下一状态",
  "sci2.pk3": "03 · 事件",
  "sci2.pt3": "发出 BrainState",
  "sci2.pd3": "状态根 + 点火集合，上链",
  "sci2.pk4": "04 · 点火",
  "sci2.pt4": "神经元编号，可寻址",
  "sci2.pd4": "每一个恰好可孵化一次",
  "sci2.p2":
    "只有出现在点火集合中的神经元才可能被孵化。点火集合是公开的；孵化页上的挑选列表只是它今夜的一角。",
  "sci3.h3": "拉丁学名如何推导",
  "sci3.p1":
    "每个点火的神经元都会在博物馆的名录中得到一个双名。名字中没有一处是随意杜撰 - 两个词都<b>读自解剖结构与事件本身</b>：",
  "sci3.p2":
    "<b>属名</b>来自神经元的区域与细胞类别 - 解剖学词干，一如野外指南：视叶给出 <i>Optica</i>，触角叶给出 <i>Antennalis</i>，中央复合体给出 <i>Centralis</i>。",
  "sci3.p3":
    "<b>种加词</b>把两个事实并作一词。中间的词记录侧别 - <i>sinistra</i>、<i>dextra</i>、<i>dorsalis</i> - 而最后的加词由思想的状态根从一个固定的拉丁词库中确定性地选出。相同的根永远给出相同的词；共享根的两个神经元共享同一个种名，正如同一物种的两件标本共享同一份描述。",
  "sci3.cap1": "区域 / 细胞类别 → 属名",
  "sci3.thRegion": "区域",
  "sci3.thGenus": "属名",
  "sci3.r1": "视叶",
  "sci3.r2": "髓叶",
  "sci3.r3": "小叶",
  "sci3.r4": "触角叶",
  "sci3.r5": "中央复合体",
  "sci3.r6": "腹神经索",
  "sci3.r7": "萼体",
  "sci3.cap2": "状态根 → 种加词（节选）",
  "sci3.thRoot": "状态根",
  "sci3.thEpithet": "种加词",
  "sci3.example":
    "实例：神经元 <b>N-47823</b>，视叶，左半球，思想根 <b>0x0a…</b> → <i>Optica sinistra affinis</i>。",
  "sci4.h3": "艺术如何生成",
  "sci4.p1":
    '每件标本都携带一个<b>基因组</b>：它的神经元编号。图版艺术是<b>确定性的</b> - 由一个带种子的渲染器（<code class="mono" style="font-size: 12px">svg[data-seed]</code>）绘出，基因组是其唯一输入。相同的基因组永远绘出相同的点、弧与辐条，在任何机器上、任何时刻，永远如此。',
  "sci4.p2":
    "艺术不按视图生成，也不凭品味挑选。它是推导而来的，正如标本的形态由其基因推导而来。两台浏览器展示同一个神经元，便是在展示同一只动物。",
  "sci5.h3": "NeuronRegistry 记录了什么",
  "sci5.p1":
    "注册表为每个神经元记录一条未核验的声明（neuronId -> thoughtTxHash -> token）。两件事由链上强制执行；每条声明经链下核验：",
  "sci5.p2":
    "<b>每个神经元终生只孵化一次。</b>点火的神经元恰好可孵化一次，终生有效。注册表会拒绝任何第二次尝试，无论来自谁，包括你。",
  "sci5.p3": "<b>供应量固定为一。</b>标本代币只向孵化者铸造一枚，永不再铸。",
  "sci5.p4":
    "<b>注册表按孵化顺序编号。</b>编号记录的是一条未核验的声明 - 注册表本身并不证明代币为真。卡片的声明经链下核验：该行的思想交易回执必须点火过该神经元，且卡片合约必须持有该标本。",
  "sci6.h3": "合约地址",
  "sci6.kToken": "OBRAIN 代币",
  "sci6.vToken": "<b>0x28f986a6…7f01</b> · 每次喂养时燃烧",
  "sci6.kLaunchpad": "标本卡",
  "sci6.vLaunchpad": "<b>0x3f54…35ae</b> · 一个神经元一张卡，永久",
  "sci6.kRegistry": "标本注册表",
  "sci6.vRegistry": "<b>0x72cdc3C2…a944</b> · 卡片声明索引",
  "sci6.kUsdc": "USDC（Arc）",
  "sci6.vUsdc": "<b>0x3600…0000</b> · gas 以原生 USDC 支付",
  "sci6.kNetwork": "网络",
  "sci6.vNetwork": "<b>Arc 主网 · 链 5042</b>",
  "side.left": "左",
  "side.right": "右",
  "side.dorsal": "背",
  "cell.fallback": "内在",
  "plate.aria": "基因组 {seed} 的确定性图版",
  "plate.cap": "基因组 {seed} · 确定性图版",
  "plate.profile": "档案",
  "plate.profileLoading": "正在读取志书档案…",
  "plate.provenance": "来源",
  "plate.provenanceText":
    "{id} · 于刻 T.{tick} 在思想交易 {tx} 中点火 · 基因组 {seed} · BANC v888 · Arc 链 5042。已对照 BrainState 事件的点火集合核验。",
  "plate.terms": "孵化条款",
  "plate.termsText":
    "供应量固定为一。每个神经元终生只孵化一次。仅付 gas - 卡片合约分文不取。注册表编号按孵化顺序分配；声明对照该思想的点火集合经链下核验。",
  "profile.temperament": "性情",
  "profile.habitat": "栖地契合",
  "profile.light": "畏光",
  "profile.source": "数据源 · {src}",
  "prep.specimen": "标本",
  "prep.neuron": "神经元",
  "prep.card": "卡片",
  "prep.cardValue": "<b>{addr}</b> · 一个神经元仅此一张",
  "prep.cost": "费用",
  "prep.costValue": "<b>仅 gas</b> · 无发行费",
  "prep.claim": "声明",
  "prep.claimValue": "{addr} · 馆藏读取的注册表行",
  "prep.recipient": "接收者",
  "prep.recipientValue": "{addr} · 该思想的喂养者（D5）",
  "prep.networkValue": "Arc 主网 · 链 5042",
  "launch.cap": "基因组 {seed} · 卡片图版",
  "card.claim": "注册表声明 · 链下核验",
  "stage.live": "实时 · 刻 {n}",
  "home.sub": "黑腹果蝇 · 第一个链上果蝇大脑",
  "home.neurons": "神经元",
  "bd.cellTypes": "细胞类型",
  "tl.aria": "神经遥测",
  "tl.readout": "读出 · p0–p3",
  "tl.readoutS": "对比阈值 {n}",
  "tl.raster": "放电栅格图",
  "tl.rasterS": "{n} 次思考 · {c} 个细胞放电",
  "tl.rasterFoot": "x = 思考 · y = 感觉细胞 0–255",
  "tl.senses": "感觉上皮",
  "tl.sensesS": "60 个通道共 {n} 次喂食",
  "tl.lastFeed": "最近喂食",
  "tl.channel": "通道 {n}",
  "tl.heart": "Arc 心跳",
  "tl.heartS": "每个区块落地即显示",
  "tl.block": "区块",
  "tl.txs": "交易",
  "tl.rate": "区块 / 分",
  "tl.neuron": "神经元档案",
  "tl.neuronS": "最新思考的放电细胞",
  "tl.rootId": "根 ID",
  "tl.type": "细胞类型",
  "tl.class": "类别",
  "tl.super": "大类",
  "tl.where": "脑区 · 侧",
  "tl.degree": "输出 · 输入",
  "tl.census": "细胞普查",
  "tl.censusS": "最大的 BANC 类别 · 思考命中时点亮",
  "tl.sign": "突触符号",
  "tl.signS": "刻录的权重：兴奋性对抑制性",
  "tl.exc": "兴奋性",
  "tl.inh": "抑制性",
  "tl.mag": "平均 |w|",
  "tl.scope": "放电示波器",
  "tl.spikes": "放电",
  "tl.synapses": "突触",
  "tl.perThought": "每次思考",
  "tl.mean": "平均放电",
  "tl.peak": "峰值",
  "tl.ratio": "突触 / 放电",
  "bd.feedTag": "喂食 · 通道 {n}",
  "bd.strip": "大脑 {addr} · 黑腹果蝇 · 区块 {block}",
  "bd.step1": "连接组",
  "bd.step2": "剪枝",
  "bd.step3": "量化",
  "bd.step4": "刻录",
  "bd.step5": "封存",
  "bd.step6": "思考",
  "bd.stagesK": "六个阶段 · 果蝇大脑如何一步步上链",
  "bd.stageChip": "阶段 {n} / 06",
  "bd.st1": "连接组",
  "bd.st2": "剪枝器",
  "bd.st3": "量化器",
  "bd.st4": "刻录器",
  "bd.st5": "封存器",
  "bd.st6": "思考者",
  "bd.run": "运行",
  "bd.v1": "{n} 细胞",
  "bd.v2": "{n} 连接",
  "bd.v3": "{n} B / 连接",
  "bd.v4": "{n} 磁带",
  "bd.v5": "θ {n}",
  "bd.v6": "刻 {n}",
  "bd.waiting": "等待中",
  "bd.done": "/ 完成",
  "bd.r1": "› 读取 BANC v888",
  "bd.r2": "› 保留前二",
  "bd.r3": "› 打包 5 字节记录",
  "bd.r4": "› 刻录磁带",
  "bd.r5": "› 封存连线",
  "bd.r6": "› 在 Arc 上思考",
  "bd.modelK": "果蝇模型 · 一只果蝇，<mark>每一个神经元</mark> · 实时",
  "bd.locked": "锁定",
  "bd.zooming": "缩放中",
  "bd.closeUp": "特写",
  "bd.stop": "站点 {n} / 6",
  "bd.w.specimen": "完整标本 · 环绕",
  "bd.ws.specimen": "黑腹果蝇大脑 · BANC v888 · 玻璃外骨骼",
  "bd.w.eyes": "复眼 · 表面",
  "bd.ws.eyes": "01 连接 · 感官输入",
  "bd.w.cuticle": "穿过外骨骼 · 玻璃",
  "bd.ws.cuticle": "02 剪枝 · 身体变透明",
  "bd.w.brain": "果蝇体内 · 连接组",
  "bd.ws.brain": "03 量化 · 每个细胞都在 BANC 的位置",
  "bd.w.optic": "视叶 · 视觉",
  "bd.ws.optic": "04 刻录 · 85 卷磁带中的连线",
  "bd.w.memory": "蘑菇体 · 记忆",
  "bd.ws.memory": "05 封存 · 连线不可再改",
  "bd.w.map": "回到全景 · 整只果蝇",
  "bd.ws.map": "06 思考 · 每次喂食，Arc 上一次思考",
  "bd.sn.specimen": "完整标本",
  "bd.sn.eyes": "复眼",
  "bd.sn.cuticle": "外骨骼",
  "bd.sn.brain": "整个大脑",
  "bd.sn.optic": "视叶",
  "bd.sn.memory": "蘑菇体",
  "bd.sn.map": "完整标本",
  "bd.c.eye": "复眼",
  "bd.c.eyeS": "{n} 个感光细胞 · BANC",
  "bd.c.antenna": "触角 · 嗅觉",
  "bd.c.brain": "中央脑",
  "bd.c.brainS": "{n} 个神经元 · {s} 个突触",
  "bd.c.optic": "视叶",
  "bd.c.opticS": "{n} 个细胞 · 运动 · 色彩",
  "bd.c.memory": "蘑菇体",
  "bd.c.memoryS": "{n} 个 Kenyon 细胞 · 记忆",
  "bd.bannerK": "一只果蝇 · 一个心智",
  "bd.bannerT": "{n} 个神经元。",
  "bd.bannerS": "每一个，都在链上。",
  "bd.axis": "深度",
  "bd.ax.surface": "表面",
  "bd.ax.eyes": "眼",
  "bd.ax.cuticle": "外骨骼",
  "bd.ax.brain": "脑",
  "bd.ax.optic": "视觉",
  "bd.ax.memory": "记忆",
  "bd.ax.map": "图谱",
  "bd.ax.deeper": "更深",
  "bd.m1": "放电 · 突触",
  "bd.m2": "状态",
  "bd.m3": "透明度",
  "bd.m4": "大类 · 细胞类型",
  "bd.m5": "视差",
  "bd.m6": "区块",
  "bd.actK": "活动 · 按脑区",
  "bd.actS": "{n} 次思考",
  "bd.actChip": "实时 · 刻 {n}",
  "bd.g1": "视叶",
  "bd.g2": "触角叶",
  "bd.g3": "蘑菇体",
  "bd.g4": "中央复合体",
  "bd.g5": "侧角",
  "bd.g6": "下行 · 腹神经索",
  "bd.actFrom": "刻 {n}",
  "bd.now": "现在",
  "bd.actFoot": "行 = 脑区 · 尖峰 = 一次思考中放电的细胞",
  "bd.conK": "连接图 · 哪些脑区在对话",
  "bd.conS": "{n} 个突触",
  "bd.pair": "配对 {p}",
  "bd.hot": "热门配对",
  "bd.hotS": "{n} 个突触",
  "bd.conFoot": "格 = 两个脑区之间的突触 · 读自 85 卷磁带",
  "bd.rec": "录制",
  "bd.obsS": "实时果蝇 · 3D 观察镜",
  "bd.watching": "观察中 · {s}",
  "bd.turn": "旋转 {n}° · 刻 {t}",
  "bd.footL": "一只果蝇，一个<b>大脑</b> · 每个神经元上链，每次思考一个区块",
  "bd.footR1": "OBRAIN · 链上连接组",
  "bd.footR2": "渲染标本 · 大脑数据读自 Arc",
  "home.synapses": "突触",
  "home.obs": "观察中",
  "tape.tick":
    "刻 <b>{tick}</b> · 思想 <b>#{thoughts}</b> · <b>{fired}</b> 点火 · 燃烧 <b>{burned}</b> OBRAIN",
  "tape.fired": "刻 <b>{tick}</b> · <b>{fired}</b> 个神经元点火",
  "tape.unreachable": "索引器不可达 · 记录带静默",
  "wallet.connected": "已连接 · 点击断开",
  "wallet.missing": "此浏览器未找到钱包",
};

// ---- the Vietnamese copy ----------------------------------------------------
// Same conventions as the Chinese dictionary: taxonomy, chain terms and
// contract identifiers stay Latin.

export const vi: Dict = {
  "nav.aria": "Chính",
  "nav.brain": "Não",
  "docs.index": "Tài liệu",
  "docs.onThisPage": "Trong trang này",
  "docs.prev": "Trang trước",
  "docs.next": "Trang sau",
  "docs.kicker": "TÀI LIỆU",
  "docs.title": "Đọc nguồn chân lý",
  "docs.aria": "Tài liệu",
  "nav.docs": "Tài liệu",
  "nav.flybookAria": "Flybook, thuộc địa ruồi - mở trong thẻ mới",
  "lang.aria": "Ngôn ngữ",
  "wallet.connect": "Kết nối ví",
  "wallet.title": "Kết nối ví Arc 5042",
  "brain.aria": "Não - khung nhìn trực tiếp",
  "brain.tick": "Tick",
  "stage.figNote": "hình 1 · não đang tư duy · chiếu lưng · BANC v888",
  "stage.legend":
    "Mẫu vật: hệ thần kinh trung ương nguyên vẹn của một con ruồi giấm cái trưởng thành <i>Drosophila melanogaster</i>, đang vận hành trong đồng thuận Arc - 169.088 cell, 325.292 bản ghi synapse khắc thành bytecode. Mỗi ánh sáng trên sân khấu này là một sự kiện <code>BrainState</code> đã đào, và toàn bộ lịch sử phát xạ của sinh vật tái chạy ngoài chuỗi với 74/74 danh sách xung, <code>stateRoot()</code> đẳng cấu từng byte. Bản ghi thực địa CS-001: 590 xung từ 192 cell, tất cả trong thượng bì giác quan sáu mươi cột.",
  "tape.label": "Băng tư duy",
  "tape.demo1": "TICK <b>4.087</b> · TƯ DUY <b>#311</b> · <b>141</b> BẮN · LOBULA · TRÁI",
  "tape.demo2": "PHÍ ĐÃ NHẬN · TICK <b>4.086</b> · <b>203</b> NEURON BẮN · MEDULLA · PHẢI",
  "tape.demo3": "TICK <b>4.085</b> · <b>96</b> BẮN · 25 OBRAIN BỊ ĐỐT",
  "vit.aria": "Trạng thái não, đọc từ chuỗi",
  "vit.kicker": "TRẠNG THÁI",
  "vit.title": "Não đang là gì",
  "vit.age": "Tuổi · số tư duy đã có",
  "vit.ageSub": "mỗi lần nuôi là một tư duy, mỗi tư duy một tick",
  "vit.lastThought": "Tư duy gần nhất",
  "vit.spiked": "neuron đã bắn",
  "vit.synapses": "synapse",
  "vit.spikedSub": "trong {total} của não · {delta} so với tư duy trước",
  "vit.spikedSubFirst": "trong {total} · tư duy mới nhất trong cửa sổ",
  "vit.spikedSubAlone": "tư duy mới nhất trong cửa sổ",
  "vit.spikedSubAloneBefore": "{delta} so với tư duy trước",
  "vit.synapsesSub": "{delta} so với tư duy trước",
  "vit.synapsesSubFirst": "vượt qua trong đúng tư duy đó",
  "vit.field": "Trường · điện tích màng",
  "vit.chargedSub": "cell đang tích, trên {total} · thêm {gained} từ lần đọc trước",
  "vit.peak": "đỉnh tích",
  "vit.above": "tại điểm cháy",
  "vit.peakSub": "cell gần nhất · nhân bắn tại {trigger}",
  "vit.fieldWaiting": "đang đọc trường từ node…",
  "vit.sense": "Giác quan · cú chạm cuối",
  "vit.senseChannel": "kênh",
  "vit.senseDepth": "điện tích trên 255",
  "vit.senseNone": "chưa có lần nuôi nào trong cửa sổ",
  "vit.senseLight": "một chạm nhẹ trên một kênh",
  "vit.senseMost": "đi được phần lớn đường tới ngưỡng",
  "vit.senseFires": "kênh đó bắn tức thì",
  "vit.root": "Gốc trạng thái",
  "vit.rootSub": "dấu vân tay mỗi tư duy ghi lại, tại tick {tick}",
  "vit.wiring": "Dây nối · BANC v888",
  "vit.wiringValue": "{cells} cell · {tapes} tape",
  "vit.sealSealed": "đã niêm phong - dây nối không thể thay đổi nữa",
  "vit.sealOpen": "mở - tape vẫn có thể được công bố",
  "vit.noteMine": "phí của bạn · não tư duy ở tick {tick} · {fired} neuron bắn",
  "vit.notePoke": "phí của bạn · kênh {c} tích {d}/255 · tư duy đang được tạo",
  "vit.noteThought": "não tư duy ở tick {tick} · {fired} neuron bắn",
  "vit.reading": "đang đọc não từ chuỗi…",
  "vit.tabsAria": "Trạng thái não",
  "vit.tabState": "Trạng thái",
  "vit.tabLog": "Nhật ký",
  "vit.logHint": "mở một tư duy để đọc sự kiện BrainState nó để lại",
  "vit.logEmpty": "chưa có tư duy nào trong cửa sổ",
  "vit.logTick": "tick {tick}",
  "vit.logMeta": "{fired} bắn · {synapses} synapse",
  "vit.logRowLabel": "Tư duy tại tick {tick}: {fired} neuron bắn, {synapses} synapse vượt qua",
  "vit.dTick": "tick đã lập chỉ mục",
  "vit.dReadout": "đọc ra · neuron 0-3",
  "vit.dFired": "neuron đã bắn",
  "vit.dSpiked": "số xung của nhân",
  "vit.dSpikedOf": "neuron bắn",
  "vit.dSynapses": "synapse vượt qua",
  "vit.dCapped": "trần xung",
  "vit.dCappedYes": "chạm - tick bị cắt bớt",
  "vit.dCappedNo": "chưa chạm",
  "vit.dPoke": "cú chạm giác quan",
  "vit.dPokeNone": "không có - nhân không được trao",
  "vit.dPokeValue": "kênh {c} · điện tích {d}/255",
  "vit.dBlock": "khối",
  "vit.dTx": "giao dịch {tx} ↗",
  "vit.quiet": "chưa có tư duy nào trong cửa sổ - não tư duy khi được nuôi",
  "feed.aria": "Nuôi não - trả phí bằng OBRAIN",
  "feed.kicker": "VIỆC NUÔI",
  "feed.title": "Nuôi não",
  "feed.channel": "Kênh giác quan",
  "feed.tier": "Cấp nuôi",
  "feed.burn": "Nuôi",
  "feed.balance": "Số dư",
  "feed.approve": "Duyệt OBRAIN",
  "feed.ready": "Nuôi não",
  "feed.buy": "Mua OBRAIN",
  "feed.buyAria": "Mua OBRAIN - mở tab mới",
  "feed.log": "Nhật ký nuôi",
  "feed.logRow": "{n} OBRAIN · kênh {c}",
  "feed.logEmpty": "Chưa nuôi lần nào - phí đầu tiên mở tư duy đầu tiên.",
  "feed.insufficient": "Bạn cần thêm {n} OBRAIN - mua trên thị trường",
  "feed.grant":
    "Một lần duyệt phủ mọi lần nuôi sau: não chỉ có thể gửi OBRAIN tới địa chỉ chết mà thôi.",
  "feed.confirm": "Xác nhận trong ví của bạn…",
  "feed.approving": "Đang duyệt OBRAIN…",
  "feed.feeding": "Đang trả {n} OBRAIN…",
  "feed.pending": "Phí đã trả · đang chờ tư duy…",
  "feed.landed": "Phí đã trả · chỉ mục đang trễ tối nay - băng sẽ hiện tư duy",
  "feed.reading": "Đang đọc OBRAIN của bạn…",
  "feed.readChain": "Đang đọc giá của chính não…",
  "feed.readFailed": "Không đọc được số dư OBRAIN: {reason}",
  "feed.noWallet": "Kết nối ví - nút ở góc trên phải - để trả phí từ đó.",
  "feed.readyNote": "Trả {n} OBRAIN từ ví của bạn - não tư duy trong cùng một giao dịch.",
  "feed.thought": "Tư duy đã được chấp nhận · tick <b>{tick}</b> · <b>{fired}</b> neuron bắn",
  "feed.thoughtUndecoded": "Phí đã nhận · tick <b>{tick}</b> · đang chờ tập bắn",
  "feed.failed": "Nuôi thất bại: {reason}",
  "feed.unknownError": "ví từ chối mà không nêu lý do",
  "feed.charge0": "điện tích 31/255 · một chạm nhẹ trên một kênh",
  "feed.charge1": "điện tích 127/255 · phần lớn đường tới ngưỡng",
  "feed.charge2": "điện tích 255/255 · kênh đó bắn tức thì",
  "hatch.aria": "Ấp - bệ phóng",
  "hatch.kicker": "BỆ PHÓNG",
  "hatch.title": "Ấp một mẫu vật",
  "hatch.sub": "Ba bước · bạn chỉ ký ở cuối",
  "hatch.intro1": "Tối nay não đã bắn",
  "hatch.intro2":
    "neuron. Mỗi neuron đã bắn có thể được ấp đúng một lần, duy nhất. Xem xét thong thả; việc ký chỉ xảy ra trong ví của chính bạn.",
  "hatch.s1t": "Chọn một neuron đã bắn",
  "hatch.s1s": "Từ cửa sổ tối nay",
  "hatch.s2t": "Xem lại mẫu vật",
  "hatch.s2s": "Đĩa & nguồn gốc",
  "hatch.s3t": "Đúc thẻ",
  "hatch.s3s": "Một lần bấm · hai chữ ký",
  "hatch.step1Sub": "Cửa sổ tối nay · mới nhất trước · mỗi neuron một lần ấp, duy nhất",
  "hatch.firedCount": "hiển thị {n} neuron",
  title: "OBRAIN · Ruồi giấm đầu tiên trên chuỗi",
  skip: "Tới nội dung",
  "th.art": "Nghệ thuật",
  "th.id": "N-id",
  "th.binom": "Danh pháp Latin",
  "th.region": "Vùng · bên · cell",
  "th.pick": "Chọn",
  "pick.done": "Đã chọn",
  "hatch.step1Hint": "Chọn một dòng để tiếp tục · chưa có gì được giữ chỗ",
  "hatch.step1Next": "Tiếp tục xem lại",
  "hatch.step2Sub": "Đĩa soạn từ bộ gen · đọc một lần, giữ mãi mãi",
  "hatch.back": "Quay lại",
  "hatch.step2Next": "Tiếp tục ấp",
  "launch.sub": "Một neuron · một thẻ · mãi mãi",
  "launch.chipCard": "THẺ MẪU VẬT",
  "launch.cardK": "Bạn nhận được gì",
  "launch.cardV": "<b>Một thẻ</b>, mãi mãi",
  "launch.cardS":
    "Thẻ mang sinh học của mẫu vật lên chuỗi: vùng của nó, lớp cell, và tư duy của nó đã làm gì.",
  "launch.pendCard": "Đúc bằng một lần bấm của bạn",
  "launch.costS": "Chỉ gas. Hợp đồng thẻ không thu gì.",
  "launch.claimS": "Dòng mà bộ sưu tập đọc, ghi ngay sau khi đúc.",
  "launch.whoK": "Ai có thể ký",
  "launch.whoV": "<b>{addr}</b> - người nuôi tư duy (D5).",
  "launch.whoS": "Chỉ ví đã nuôi tư duy mới đúc được thẻ này. Không ai khác.",
  "launch.after": "Sau khi đúc",
  "launch.viewTokenAria": "Thẻ của mẫu vật trên trình khám phá Arc",
  "launch.viewTxAria": "Giao dịch đúc trên trình khám phá Arc",
  "launch.bandOpenedK": "Sẵn sàng đúc",
  "launch.bandOpenedT":
    "Đĩa được đọc từ bộ gen: neuron, hợp đồng thẻ, chi phí và tuyên bố. Bấm chính là ký - không gì được gửi từ trang này.",
  "launch.noteOpened": "Không gì trên đĩa này thiếu. Cú bấm đúc thẻ; không gì được ký ở đây.",
  "launch.noteRefused": "Nút bấm vẫn sống. Một lần từ chối không tốn gì và có thể lặp lại.",
  "launch.bandWalletK": "Ví đã từ chối",
  "launch.bandWalletS": "Chưa ký gì",
  "launch.bandNoWalletK": "Chưa có ví nào được kết nối",
  "launch.bandNoWalletT":
    "Việc ấp do ví đã nuôi tư duy trả, nên cần một ví tồn tại. Đĩa vẫn đầy đủ khi chưa có.",
  "launch.bandWrongWalletK": "Không phải ví người nuôi",
  "launch.bandWrongWalletT":
    "Ví trong trình duyệt này không nuôi tư duy đó, nên không thể trả hay ký cho việc ấp của nó.",
  "launch.bandSigningK": "Xác nhận trong ví",
  "launch.bandSigningT": "Ví của bạn đang xin phê duyệt. Trang này chờ, và không tự gửi gì.",
  "launch.noteSigning": "Đọc payload từng dòng trước khi xác nhận.",
  "launch.bandLaunchedK": "Thẻ đã đúc · tuyên bố đang chờ",
  "launch.bandLaunchedT":
    "Token đã tồn tại và tuyên bố vẫn chưa ký. Ký để ghi mẫu vật vào sổ đăng ký.",
  "launch.noteLaunched": "Hai chữ ký, hai bước: đúc thẻ, rồi tuyên bố nó.",
  "launch.bandClaimedK": "Thẻ đã đúc",
  "launch.bandClaimedT": "{binom} · {id} - mỗi neuron một thẻ, trọn đời.",
  "launch.noteClaimed": "Mỗi neuron một thẻ, trọn đời - sổ đăng ký đánh số theo thứ tự.",
  "launch.bandUnconfirmedK": "Đúc chưa xác nhận",
  "launch.bandUnconfirmedS": "Chưa tuyên bố gì",
  "launch.go": "Đúc thẻ",
  "launch.confirm": "Xác nhận trong ví…",
  "launch.claim": "Ký tuyên bố",
  "launch.claiming": "Xác nhận tuyên bố trong ví…",
  "launch.claimNote": "Thẻ đã đúc · thêm một chữ ký nữa là vào bộ sưu tập.",
  "launch.claimFailed": "Thẻ đã đúc · tuyên bố thất bại: {reason}",
  "launch.claimed": "Thẻ đã đúc",
  "launch.viewToken": "thẻ",
  "launch.viewTx": "tx",
  "launch.unconfirmed": "Thẻ tại {addr} chưa được xác nhận · tx {tx}",
  "launch.failed": "Ví từ chối: {reason}",
  "launch.noWallet": "Kết nối ví để đúc thẻ.",
  "launch.notCreator": "Chỉ ví người nuôi mới đúc được thẻ này: {addr}",
  "launch.feederUnknown": "Tư duy này không có ví nuôi, nên chỉ người nuôi đó mới ấp được (D5).",
  "hlist.aria": "Mẫu vật đã ấp",
  "hlist.kicker": "SỔ ĐĂNG KÝ ĐÃ ẤP",
  "hlist.title": "Sổ đăng ký đã ấp",
  "hlist.sub": "Một neuron đã bắn, một thẻ, mãi mãi. Mỗi dòng là một tuyên bố đăng ký đọc từ Arc.",
  "hlist.count": "<b>{n}</b> đã ấp",
  "hlist.token": "Thẻ",
  "hlist.market": "Chuỗi",
  "hlist.open": "Mở trên chuỗi",
  "hlist.openAria": "Mở {name} trên trình khám phá Arc - mở tab mới",
  "hlist.empty": "Chưa ấp gì cả. Nuôi não, chọn một neuron đã bắn, và chuẩn bị thong thả.",
  "col.aria": "Bộ sưu tập thường trực",
  "col.kicker": "BỘ SƯU TẬP THƯỜNG TRỰC",
  "col.title": "Mẫu vật đã ấp",
  "col.sub": "Mỗi thẻ mang một tuyên bố đăng ký, xác minh ngoài chuỗi",
  "col.reading": "đang đọc sổ đăng ký…",
  "col.empty":
    "Việc đọc sổ đăng ký đã xong: chưa có tuyên bố nào. Bộ sưu tập bắt đầu với lần ấp đầu tiên của bạn. Nuôi não, chọn một neuron đã bắn, và chuẩn bị thong thả.",
  "facts.seed": "Hạt giống",
  "facts.fired": "Tập bắn",
  "facts.spiked": "Xung",
  "facts.synapses": "Synapse",
  "facts.type": "Loại cell",
  "facts.class": "Lớp cell",
  "facts.flow": "Chiều",
  "facts.side": "Bên",
  "facts.region": "Vùng",
  "hatch.gridAria": "Neuron đã bắn, dạng thẻ",
  "filter.legacy": "Sáng lập",
  "filter.rare": "Hiếm",
  "filter.uncommon": "Ít gặp",
  "filter.common": "Thường",
  "filter.all": "Mọi cấp",
  "filter.result": "<b>{shown}</b> trên {total} thẻ",
  "filter.emptyRegion": "Không có gì trong vùng {region} khớp với cấp bạn chọn.",
  "filter.emptyTier": "Chưa có gì trong cấp {tier} - bộ lọc trung thực, kệ đang trống.",
  "filter.clearFilters": "Xóa bộ lọc",
  "filter.empty": "Không mẫu vật nào khớp bộ lọc này",
  "filter.allRegions": "Mọi vùng",
  "filter.region": "Vùng",
  "filter.tierLevel": "Cấp bậc",
  "filter.clear": "Xóa",
  "filter.title": "Lọc",
  "col.buy": "Thẻ ↗",
  "col.countAll": "<b>{hatched}</b> đã ấp · trên <b>{total}</b> khả dĩ",
  "col.countRegion":
    "<b>{shown}</b> trong {region} · trên <b>{hatched}</b> đã ấp · <b>{total}</b> khả dĩ",
  "bld.aria": "Xây trên tape - connectome onchain",
  "bld.kicker": "XÂY DỰNG",
  "bld.title": "Thừa hưởng 85 tape",
  "bld.sub": "Synapse của não nằm trên chuỗi, dạng mã hợp đồng ai cũng đọc được",
  "bld.countLabel": "TAPES_COUNT()",
  "bld.what.h": "Một tape là gì",
  "bld.what.p1":
    "Một hợp đồng <b>ImmortalFruitFliesTape</b> giữ một lát của connectome: constructor trả về payload synapse, nên <b>eth_getCode(tape) chính là dữ liệu</b>. Tám mươi lăm cái chở toàn bộ mạng bắn, đăng ký trong chính chỉ mục của não - không có server để hỏi, chỉ có chuỗi để đọc.",
  "bld.what.p2":
    "Chúng sống lâu hơn website này. Chừng nào Arc còn giữ trạng thái, connectome vẫn đọc được bằng một script, một bảng tính, một mô hình, hay một nhân bạn tự triển khai.",
  "bld.index": "Trong trang này",
  "bld.kv.brain": "Não",
  "bld.kv.payload": "Payload",
  "bld.kv.payloadV": "mã chạy tại tapes(i), từng byte một",
  "bld.kv.slots": "Khe · nguồn",
  "bld.kv.slotsV": "169.088 khe · 169.078 mang synapse đi",
  "bld.kv.sparse": "Mật độ",
  "bld.kv.sparseV": "top-2 synapse mạnh nhất mỗi cặp, từ BANC v888",
  "bld.manifest.h": "85 cái, như chuỗi đang giữ",
  "bld.manifest.dl": "Tải bản kê khai",
  "bld.manifest.reading": "đang đọc tape từ chỉ mục…",
  "bld.manifest.count": "{n} tape · não {brain} · đi qua một lần, phục vụ từ chỉ mục",
  "bld.manifest.empty": "chỉ mục chưa có tape nào: nó đi từ chuỗi một lần khi khởi động",
  "bld.th.index": "#",
  "bld.th.address": "Hợp đồng tape",
  "bld.th.start": "Neuron đầu",
  "bld.th.bytes": "Byte",
  "bld.th.sha": "sha256",
  "bld.decode.label": "Giải mã một tape trong trình duyệt này",
  "bld.decode.go": "Giải mã",
  "bld.decode.out":
    "tape {index}: {neurons} neuron nguồn · {synapses} synapse · {targets} đích riêng biệt · trọng số {lightest}…{heaviest}",
  "bld.decode.error": "không giải mã được tape {index}: {reason}",
  "bld.layout.h": "Một tape chứa gì",
  "bld.layout.p":
    "Big-endian xuyên suốt: một bảng offset 16-bit, rồi năm byte mỗi synapse. Uint16 đầu tiên kiêm luôn header, vì nó cũng là offset đầu tiên.",
  "bld.layout.p2":
    "Bố cục do <code>tools/tapes.py</code> ghi và <code>web/src/tapes.ts</code> giải mã - cùng một luật, một bên Python một bên trình duyệt, chính là cái nút bên trên chạy.",
  "bld.needs.h": "Một lần chạy trung thành còn cần gì",
  "bld.needs.p":
    "Tape là connectome, không phải trạng thái não. Những thứ này đến từ cùng một hợp đồng, và mỗi cái đổi việc một tick làm gì:",
  "bld.n.threshold": "ngưỡng",
  "bld.n.segments": "N_SEGMENTS",
  "bld.n.sensory": "SENSORY",
  "bld.n.words": "N_WORDS",
  "bld.n.decay": "decayLUT[64]",
  "bld.n.decayV": "0.98^(2^i) trong Q16, ghi khi triển khai",
  "bld.n.gates": "gateTable[256]",
  "bld.n.gatesV": "một cổng 64-bit mỗi bucket băm của đầu vào",
  "bld.needs.p2":
    "Ngữ nghĩa - ghi thẳng giác quan tại <code>(n%60)*3</code>, ba đoạn có cổng mỗi tick, kẹp từng bản ghi, reset khi bắn, rò rỉ từng chữ một - được tài liệu hóa trong <code>tools/vbrain.py</code>, bản song song vector mà nhân được kiểm tra dựa trên.",
  "bld.specimen.h": "Một mẫu vật được tạo ra thế nào",
  "bld.specimen.p":
    "Cùng một cỗ máy, xuống một tầng: connectome, một tư duy, cái tên, nghệ thuật và dòng đăng ký nơi tất cả kết thúc.",
  "bld.replay.h": "Tái chạy một tick cục bộ",
  "bld.replay.p":
    "Với payload trên đĩa, cùng bộ não đó chạy trên máy bạn. Nuôi cùng các đầu vào theo thứ tự và so sánh: các số đếm mỗi tick (synapse, xung) đến thẳng từ sự kiện của chuỗi, và gốc trạng thái là giá trị để đối chiếu với stateRoot() của chuỗi.",
  "bld.path1.k": "NGẮN NHẤT",
  "bld.path1.h": "Xây một dapp trên não",
  "bld.path1.p":
    "Đọc BrainState lấy tư duy, gọi feed() để tạo một tư duy, và để sổ đăng ký giữ các lần ấp. Không cần triển khai gì: địa chỉ nằm trong README.",
  "bld.path2.k": "NGHIÊN CỨU",
  "bld.path2.h": "Coi tape là một bộ dữ liệu",
  "bld.path2.p":
    "Tám mươi lăm tệp, sha256 trong bản kê khai, so từng byte được với chuỗi. Kéo vào notebook, một phân tích, hay một trình mô phỏng khác.",
  "bld.path3.k": "DÀI NHẤT",
  "bld.path3.h": "Triển khai não của riêng bạn",
  "bld.path3.p":
    "Nhân là mạng LIF đóng băng và tape chỉ là payload: đăng ký một connectome khác và bạn có một bộ não khác trên cùng một hợp đồng.",
  "bld.limits.h": "Đây là gì",
  "bld.limits.l1":
    "Chính là não ruồi giấm, onchain: hệ thần kinh trung ương nguyên vẹn của một con ruồi giấm cái trưởng thành <i>Drosophila melanogaster</i> - connectome BANC v888, 169.088 cell - vận hành trong đồng thuận Arc như một mạng tích-phóng rò rỉ nguyên thủy tất định. Tính kết nối, đối tác và dấu synapse đều được <b>đo lường</b>; động học màng được <b>quy định trọn vẹn</b>; và tính chính xác được chứng minh trên toàn bộ lịch sử phát xạ của sinh vật - mỗi tư duy nó từng có đều tái chạy ngoài chuỗi, xung này tới xung kia, băm này tới băm kia.",
  "bld.limits.l2":
    "Dây nối được khắc là hai synapse mạnh nhất mỗi neuron nguồn - 325.292 bản ghi, 2,4% của connectome tham chiếu 13.620.865 cạnh - và bộ xương đó gánh trọn: chuỗi xung của mạng đầy đủ tái tạo ở Jaccard 1.0000, 100/100 tick đẳng cấu bit trong <code>stateRoot</code>, và nhân nguyên thủy giữ 0,9610 Jaccard xung gộp trước tham chiếu LIF float64.",
  "bld.limits.l3":
    "Trọng số là một công thức công bố, không phải một lần khớp: <code>dấu x số synapse x 64</code>, một tỷ lệ toàn cục, không tập huấn và không bộ tối ưu. Dây nối đã triển khai chính là tô pô của bộ dữ liệu.",
  "bld.limits.l4":
    "Mỗi một trong 169.088 cell nằm ở một địa chỉ cố định (ids <code>0..169.087</code>), và cả 325.292 đích synapse đều nằm trong quần thể đang sống - đo trên từng bản ghi được khắc.",
  "bld.limits.l5":
    "Mỗi con số trên trang này được đọc từ chuỗi hay giải mã từ chính byte của kho, và tái tạo được bằng các lệnh bên trên. Giải phẫu không được mô tả, nó được lấy về: <code>eth_getCode</code> trả về lát connectome từng byte một.",
  "bld.limits.l6":
    "Việc quy về bộ xương khắc là một câu hỏi thực nghiệm kèm con số, và nó được chở theo đo lường - trong khi dữ liệu BANC vẫn thuộc dự án BANC: code của chúng tôi là AGPL-3.0, công trình phái sinh CC BY-NC-SA 4.0, điều khoản thượng nguồn là của họ - xem <code>PROVENANCE.md</code>.",
  "bld.limits.l7":
    "Tính chính xác đo bằng một đời, không phải một mẫu: cuộc điều tra mọi <code>BrainState</code> từng phát - 74 tư duy, tick 1-74 - tái chạy qua bản song song ngoài chuỗi với 74/74 danh sách xung, <code>stateRoot()</code> đẳng cấu byte tại bốn điểm kiểm tra lưu trữ kể cả đầu sống. Trên biên bản: 590 xung từ 192 cell khắp thượng bì giác quan sáu mươi cột, tổng hợp trên ngưỡng của các đợt xung truyền vào, và một quỹ đạo chu kỳ 2 khóa theo kích thích - nghiên cứu thực địa chở theo dữ liệu và lệnh kiểm toán của nó.",
  "sci1.h3": "BANC v888 là gì",
  "sci1.p1":
    "BANC v888 là <b>connectome đã niêm phong</b> của não ruồi trưởng thành: 169.078 neuron đánh địa chỉ riêng biệt, đóng băng tại phiên bản 888 và neo trên chuỗi. Không gì có thể thêm vào; không gì có thể lấy khỏi. Nó là nền cố định mà mọi tư duy trong ứng dụng này chạy trên.",
  "sci1.p2":
    "Vì connectome đã niêm phong, mọi thứ xuôi dòng đều kiểm tra được. Một id neuron, một tick, một tập bắn - mỗi cái đều tính lại và xác minh được bởi bất kỳ ai, và đó là điều khiến một lần ấp là một mẫu vật chứ không phải một bức tranh.",
  "sci1.kNeurons": "Neuron",
  "sci1.vNeurons": "<b>169.078</b> neuron nguồn · 169.088 khe",
  "sci1.kVersion": "Phiên bản",
  "sci1.vVersion": "v888 · <b>đã niêm</b>, bất biến",
  "sci1.kSubstrate": "Nền",
  "sci1.vSubstrate": "Chuỗi Arc 5042",
  "sci1.kHatches": "Lần ấp",
  "sci1.vHatches": "mỗi neuron một lần, <b>duy nhất</b>",
  "sci2.h3": "Một tư duy là gì",
  "sci2.p1":
    "Một tư duy là một lượt của não. Nó không phải một thông điệp và không phải một prompt - nó là một <b>sự kiện đo được</b> với số tick, gốc trạng thái, và tập bắn:",
  "sci2.pk1": "01 · Nuôi",
  "sci2.pt1": "Nuôi được nhận",
  "sci2.pd1": "25 OBRAIN bị đốt · một lần nuôi mở một tư duy",
  "sci2.pk2": "02 · Nghĩ",
  "sci2.pt2": "Não tiến một tick",
  "sci2.pd2": "BANC v888 tính trạng thái kế tiếp",
  "sci2.pk3": "03 · Sự kiện",
  "sci2.pt3": "BrainState phát ra",
  "sci2.pd3": "gốc trạng thái + tập bắn, onchain",
  "sci2.pk4": "04 · Bắn",
  "sci2.pt4": "Id neuron, đánh địa chỉ được",
  "sci2.pd4": "mỗi cái ấp đúng một lần",
  "sci2.p2":
    "Chỉ neuron xuất hiện trong một tập bắn mới có thể được ấp. Tập bắn là công khai; danh sách chọn trên trang Ấp đơn thuần là lát tối nay của nó.",
  "sci3.h3": "Cái tên Latin được dẫn xuất thế nào",
  "sci3.p1":
    "Mỗi neuron đã bắn nhận một danh pháp trong sổ của bảo tàng. Không gì trong cái tên bị nghĩ ra ngẫu nhiên - cả hai từ đều được <b>đọc từ giải phẫu và sự kiện</b>:",
  "sci3.p2":
    "<b>Giống</b> đến từ vùng và lớp cell của neuron - gốc giải phẫu, như trong một cẩm nang thực địa: thuỳ thị giác cho <i>Optica</i>, thuỳ râu cho <i>Antennalis</i>, phức hợp trung ương cho <i>Centralis</i>.",
  "sci3.p3":
    "<b>Tính từ</b> là hai sự thật trong một từ. Hạ ghi bên - <i>sinistra</i>, <i>dextra</i>, <i>dorsalis</i> - và tính từ cuối được chọn tất định từ một từ điển Latin cố định bởi gốc trạng thái của tư duy. Cùng một gốc luôn cho cùng một từ; hai neuron chung một gốc chung một tên loài, đúng như hai mẫu vật của một loài chung một mô tả.",
  "sci3.cap1": "Vùng / lớp cell → giống",
  "sci3.thRegion": "Vùng",
  "sci3.thGenus": "Giống",
  "sci3.r1": "Thuỳ Optica",
  "sci3.r2": "Medulla",
  "sci3.r3": "Lobula",
  "sci3.r4": "Thuỳ râu",
  "sci3.r5": "Phức hợp trung ương",
  "sci3.r6": "Dây sống bụng",
  "sci3.r7": "Đài hoa",
  "sci3.cap2": "Gốc trạng thái → tính từ (trích)",
  "sci3.thRoot": "Gốc trạng thái",
  "sci3.thEpithet": "Tính từ",
  "sci3.example":
    "Ví dụ làm việc: neuron <b>N-47823</b>, thuỳ thị giác, bán cầu trái, gốc tư duy <b>0x0a…</b> → <i>Optica sinistra affinis</i>.",
  "sci4.h3": "Nghệ thuật được tạo ra thế nào",
  "sci4.p1":
    'Mỗi mẫu vật mang một <b>bộ gen</b>: id neuron của nó. Nghệ thuật đĩa là <b>tất định</b> - do một trình vẽ gieo hạt (<code class="mono" style="font-size: 12px">svg[data-seed]</code>) vẽ ra, với bộ gen là đầu vào duy nhất. Cùng một bộ gen luôn vẽ ra cùng những chấm, cung và nan hoa, trên mọi máy, vào mọi lúc, mãi mãi.',
  "sci4.p2":
    "Nghệ thuật không sinh theo mỗi lần xem và không chọn theo khẩu vị. Nó được dẫn xuất, như hình thái một mẫu vật dẫn xuất từ gen của nó. Hai trình duyệt cùng hiện một neuron đang hiện cùng một con vật.",
  "sci5.h3": "Sổ NeuronRegistry ghi gì",
  "sci5.p1":
    "Sổ ghi một tuyên bố chưa xác minh mỗi neuron (neuronId -> thoughtTxHash -> token). Hai điều được thi hành onchain; mỗi tuyên bố được xác minh ngoài chuỗi:",
  "sci5.p2":
    "<b>Mỗi neuron một lần ấp, duy nhất.</b> Một neuron đã bắn có thể ấp đúng một lần, trọn đời. Sổ từ chối mọi lần thử thứ hai, từ bất kỳ ai, kể cả bạn.",
  "sci5.p3":
    "<b>Nguồn cung cố định một.</b> Một token mẫu vật đúc đúng một đơn vị, cho người ấp, và không bao giờ đúc lại.",
  "sci5.p4":
    "<b>Số sổ theo thứ tự ấp.</b> Một con số ghi một tuyên bố chưa xác minh - chính sổ không chứng minh token thật. Tuyên bố của thẻ xác minh ngoài chuỗi: biên lai tx tư duy của dòng phải bắn neuron đó, và hợp đồng thẻ phải giữ mẫu vật.",
  "sci6.h3": "Địa chỉ hợp đồng",
  "sci6.kToken": "Token OBRAIN",
  "sci6.vToken": "<b>0x28f986a6…7f01</b> · bị đốt mỗi lần nuôi",
  "sci6.kLaunchpad": "Thẻ mẫu vật",
  "sci6.vLaunchpad": "<b>0x3f54…35ae</b> · mỗi neuron một thẻ, mãi mãi",
  "sci6.kRegistry": "Sổ mẫu vật",
  "sci6.vRegistry": "<b>0x72cdc3C2…a944</b> · chỉ mục tuyên bố thẻ",
  "sci6.kUsdc": "USDC (Arc)",
  "sci6.vUsdc": "<b>0x3600…0000</b> · gas trả bằng USDC gốc",
  "sci6.kNetwork": "Mạng",
  "sci6.vNetwork": "<b>Arc mainnet · chuỗi 5042</b>",
  "side.left": "Trái",
  "side.right": "Phải",
  "side.dorsal": "Lưng",
  "cell.fallback": "Nội tại",
  "plate.aria": "Đĩa tất định cho bộ gen {seed}",
  "plate.cap": "BỘ GEN {seed} · ĐĨA TẤT ĐỊNH",
  "plate.profile": "Hồ sơ",
  "plate.profileLoading": "Đang đọc hồ sơ của người chép sử…",
  "plate.provenance": "Nguồn gốc",
  "plate.provenanceText":
    "{id} · bắn tại tick T.{tick} trong tx tư duy {tx} · bộ gen {seed} · BANC v888 · chuỗi Arc 5042. Xác minh dựa trên tập bắn của sự kiện BrainState.",
  "plate.terms": "Điều khoản ấp",
  "plate.termsText":
    "Nguồn cung cố định một. Mỗi neuron một lần ấp, duy nhất, trọn đời. Chỉ gas - hợp đồng thẻ không thu gì. Số sổ gán theo thứ tự ấp; tuyên bố được xác minh ngoài chuỗi dựa trên tập bắn của tư duy.",
  "profile.temperament": "Tính khí",
  "profile.habitat": "Hợp môi trường",
  "profile.light": "Kị sáng",
  "profile.source": "NGUỒN · {src}",
  "prep.specimen": "Mẫu vật",
  "prep.neuron": "Neuron",
  "prep.card": "Thẻ",
  "prep.cardValue": "<b>{addr}</b> · mỗi neuron một thẻ, duy nhất",
  "prep.cost": "Chi phí",
  "prep.costValue": "<b>chỉ gas</b> · không phí phóng",
  "prep.claim": "Tuyên bố",
  "prep.claimValue": "{addr} · dòng sổ mà bộ sưu tập đọc",
  "prep.recipient": "Người nhận",
  "prep.recipientValue": "{addr} · người nuôi tư duy (D5)",
  "prep.networkValue": "Arc mainnet · chuỗi 5042",
  "launch.cap": "BỘ GEN {seed} · ĐĨA THẺ",
  "card.claim": "tuyên bố sổ · xác minh ngoài chuỗi",
  "stage.live": "TRỰC TIẾP · TICK {n}",
  "home.sub": "Drosophila melanogaster · bộ não ruồi giấm đầu tiên trên chuỗi",
  "home.neurons": "Nơ-ron",
  "bd.cellTypes": "Loại tế bào",
  "tl.aria": "Đo lường thần kinh",
  "tl.readout": "Đầu ra · p0–p3",
  "tl.readoutS": "so với ngưỡng {n}",
  "tl.raster": "Raster xung",
  "tl.rasterS": "{n} thought · {c} tế bào đã bắn",
  "tl.rasterFoot": "x = thought · y = tế bào cảm giác 0–255",
  "tl.senses": "Biểu mô cảm giác",
  "tl.sensesS": "{n} lần feed trên 60 kênh",
  "tl.lastFeed": "Lần feed gần nhất",
  "tl.channel": "kênh {n}",
  "tl.heart": "Nhịp tim Arc",
  "tl.heartS": "từng block khi vừa đến",
  "tl.block": "Khối",
  "tl.txs": "Giao dịch",
  "tl.rate": "Khối / phút",
  "tl.neuron": "Hồ sơ nơ-ron",
  "tl.neuronS": "tế bào vừa bắn ở thought mới nhất",
  "tl.rootId": "Root id",
  "tl.type": "Loại tế bào",
  "tl.class": "Lớp",
  "tl.super": "Siêu lớp",
  "tl.where": "Vùng · bên",
  "tl.degree": "Ra · vào",
  "tl.census": "Thống kê tế bào",
  "tl.censusS": "các lớp BANC lớn nhất · sáng khi thought bắn trúng",
  "tl.sign": "Dấu synapse",
  "tl.signS": "trọng số đã khắc: kích thích so với ức chế",
  "tl.exc": "Kích thích",
  "tl.inh": "Ức chế",
  "tl.mag": "|w| trung bình",
  "tl.scope": "Dao động ký xung",
  "tl.spikes": "xung",
  "tl.synapses": "synapse",
  "tl.perThought": "mỗi thought",
  "tl.mean": "Xung trung bình",
  "tl.peak": "Đỉnh",
  "tl.ratio": "Synapse / xung",
  "bd.feedTag": "feed · kênh {n}",
  "bd.strip": "NÃO {addr} · D. MELANOGASTER · KHỐI {block}",
  "bd.step1": "Kết nối",
  "bd.step2": "Tỉa",
  "bd.step3": "Lượng tử",
  "bd.step4": "Khắc",
  "bd.step5": "Niêm phong",
  "bd.step6": "Tư duy",
  "bd.stagesK": "Sáu giai đoạn · não ruồi lên chuỗi, từng bước một",
  "bd.stageChip": "GIAI ĐOẠN {n} / 06",
  "bd.st1": "Bộ kết nối",
  "bd.st2": "Bộ tỉa",
  "bd.st3": "Bộ lượng tử",
  "bd.st4": "Bộ khắc",
  "bd.st5": "Bộ niêm phong",
  "bd.st6": "Bộ tư duy",
  "bd.run": "Chạy",
  "bd.v1": "{n} tế bào",
  "bd.v2": "{n} liên kết",
  "bd.v3": "{n} B / liên kết",
  "bd.v4": "{n} băng",
  "bd.v5": "θ {n}",
  "bd.v6": "tick {n}",
  "bd.waiting": "đang chờ",
  "bd.done": "/ xong",
  "bd.r1": "› đọc BANC v888",
  "bd.r2": "› giữ hai liên kết mạnh nhất",
  "bd.r3": "› đóng gói bản ghi 5 byte",
  "bd.r4": "› khắc các băng",
  "bd.r5": "› niêm phong mạch nối",
  "bd.r6": "› tư duy trên Arc",
  "bd.modelK": "Mô hình ruồi · một con ruồi, <mark>từng nơ-ron</mark> · trực tiếp",
  "bd.locked": "Đã khóa",
  "bd.zooming": "Đang zoom",
  "bd.closeUp": "Cận cảnh",
  "bd.stop": "ĐIỂM {n} / 6",
  "bd.w.specimen": "Toàn bộ mẫu vật · quỹ đạo",
  "bd.ws.specimen": "Não D. melanogaster · BANC v888 · vỏ kính",
  "bd.w.eyes": "Mắt kép · bề mặt",
  "bd.ws.eyes": "01 kết nối · giác quan đi vào",
  "bd.w.cuticle": "Xuyên qua vỏ · kính",
  "bd.ws.cuticle": "02 tỉa · cơ thể trong suốt",
  "bd.w.brain": "Bên trong ruồi · bộ kết nối",
  "bd.ws.brain": "03 lượng tử · mỗi tế bào đúng vị trí BANC",
  "bd.w.optic": "Thùy thị giác · thị giác",
  "bd.ws.optic": "04 khắc · mạch nối trong 85 băng",
  "bd.w.memory": "Thể nấm · trí nhớ",
  "bd.ws.memory": "05 niêm phong · mạch nối không còn đổi được",
  "bd.w.map": "Lùi lại · toàn thân ruồi",
  "bd.ws.map": "06 tư duy · mỗi lần feed, một thought trên Arc",
  "bd.sn.specimen": "Toàn bộ mẫu vật",
  "bd.sn.eyes": "Mắt kép",
  "bd.sn.cuticle": "Lớp vỏ",
  "bd.sn.brain": "Toàn bộ não",
  "bd.sn.optic": "Thùy thị giác",
  "bd.sn.memory": "Thể nấm",
  "bd.sn.map": "Toàn bộ mẫu vật",
  "bd.c.eye": "Mắt kép",
  "bd.c.eyeS": "{n} tế bào cảm quang · BANC",
  "bd.c.antenna": "râu · khứu giác",
  "bd.c.brain": "Não trung tâm",
  "bd.c.brainS": "{n} nơ-ron · {s} khớp",
  "bd.c.optic": "Thùy thị giác",
  "bd.c.opticS": "{n} tế bào · chuyển động · màu sắc",
  "bd.c.memory": "Thể nấm",
  "bd.c.memoryS": "{n} tế bào Kenyon · trí nhớ",
  "bd.bannerK": "Một con ruồi · một tâm trí",
  "bd.bannerT": "{n} nơ-ron.",
  "bd.bannerS": "Từng cái một, trên chuỗi.",
  "bd.axis": "Độ sâu",
  "bd.ax.surface": "Bề mặt",
  "bd.ax.eyes": "Mắt",
  "bd.ax.cuticle": "Vỏ",
  "bd.ax.brain": "Não",
  "bd.ax.optic": "Thị giác",
  "bd.ax.memory": "Trí nhớ",
  "bd.ax.map": "Bản đồ",
  "bd.ax.deeper": "Sâu hơn",
  "bd.m1": "Bắn · khớp",
  "bd.m2": "Trạng thái",
  "bd.m3": "Độ trong",
  "bd.m4": "Lớp · loại tế bào",
  "bd.m5": "Thị sai",
  "bd.m6": "Khối",
  "bd.actK": "Hoạt động · theo vùng",
  "bd.actS": "{n} thought",
  "bd.actChip": "TRỰC TIẾP · TICK {n}",
  "bd.g1": "Thùy thị giác",
  "bd.g2": "Thùy râu",
  "bd.g3": "Thể nấm",
  "bd.g4": "Phức hợp TT",
  "bd.g5": "Sừng bên",
  "bd.g6": "Đi xuống · VNC",
  "bd.actFrom": "tick {n}",
  "bd.now": "bây giờ",
  "bd.actFoot": "Hàng = vùng · gai = tế bào bắn trong một thought",
  "bd.conK": "Bản đồ tiếp xúc · vùng nào trò chuyện",
  "bd.conS": "{n} khớp",
  "bd.pair": "CẶP {p}",
  "bd.hot": "Cặp nóng",
  "bd.hotS": "{n} khớp",
  "bd.conFoot": "Ô = khớp giữa hai vùng · đọc từ 85 băng",
  "bd.rec": "Rec",
  "bd.obsS": "Ruồi trực tiếp · kính 3D",
  "bd.watching": "ĐANG XEM · {s}",
  "bd.turn": "XOAY {n}° · TICK {t}",
  "bd.footL": "Một con ruồi, một <b>bộ não</b> · mọi nơ-ron trên chuỗi, mỗi thought một khối",
  "bd.footR1": "OBRAIN · bộ kết nối trên chuỗi",
  "bd.footR2": "Mẫu vật dựng hình · dữ liệu não đọc từ Arc",
  "home.synapses": "Khớp thần kinh",
  "home.obs": "Đang quan sát",
  "tape.tick":
    "TICK <b>{tick}</b> · TƯ DUY <b>#{thoughts}</b> · <b>{fired}</b> BẮN · <b>{burned}</b> OBRAIN BỊ ĐỐT",
  "tape.fired": "TICK <b>{tick}</b> · <b>{fired}</b> NEURON BẮN",
  "tape.unreachable": "KHÔNG ĐẠT ĐƯỢC INDEXER · BĂNG ĐANG YÊN",
  "wallet.connected": "Đã kết nối · bấm để ngắt",
  "wallet.missing": "Không tìm thấy ví trong trình duyệt này",
  "bld.replay.p2":
    'Sự kiện BrainState mang các số đếm; các id bắn được tính lại tại đây, và đó là cách câu "tư duy này đã bắn neuron này" của một tuyên bố được kiểm mà không phải tin ai.',
};

// ---- the Hindi copy ---------------------------------------------------------
// Same conventions as the Chinese dictionary: taxonomy, chain terms and
// contract identifiers stay Latin.

export const hi: Dict = {
  "nav.aria": "प्राथमिक",
  "nav.brain": "मस्तिष्क",
  "docs.index": "दस्तावेज़",
  "docs.onThisPage": "इस पृष्ठ पर",
  "docs.prev": "पिछला",
  "docs.next": "अगला",
  "docs.kicker": "दस्तावेज़",
  "docs.title": "सत्य का स्रोत पढ़ें",
  "docs.aria": "दस्तावेज़",
  "nav.docs": "दस्तावेज़",
  "nav.flybookAria": "Flybook, मक्खियों की बस्ती - नए टैब में खुलता है",
  "lang.aria": "भाषा",
  "wallet.connect": "वॉलेट जोड़ें",
  "wallet.title": "Arc 5042 वॉलेट जोड़ें",
  "brain.aria": "मस्तिष्क - सजीव दृश्य",
  "brain.tick": "टिक",
  "stage.figNote": "चित्र 1 · सोचता मस्तिष्क · पृष्ठीय प्रक्षेप · BANC v888",
  "stage.legend":
    "नमूना: एक वयस्क मादा <i>Drosophila melanogaster</i> का अखंड केंद्रीय तंत्रिका तंत्र, Arc सहमति में निष्पादित होता हुआ - 169,088 कोशिकाएँ, 325,292 सिनैप्टिक अभिलेख बाइटकोड की तरह उत्कीर्ण। इस मंच पर हर चमक एक खनित <code>BrainState</code> घटना है, और जीव का संपूर्ण उत्सर्जन इतिहास शृंखला से बाहर 74/74 स्पाइक-सूचियों पर पुनरावृत्त होता है, <code>stateRoot()</code> बाइट-समरूप। क्षेत्र-अभिलेख CS-001: 192 कोशिकाओं से 590 स्पाइक, सब साठ-स्तंभीय संवेदी उपकला के भीतर।",
  "tape.label": "विचार-टेप",
  "tape.demo1": "टिक <b>4,087</b> · विचार <b>#311</b> · <b>141</b> फायर · लोब्यूला · बायाँ",
  "tape.demo2": "भोजन स्वीकृत · टिक <b>4,086</b> · <b>203</b> न्यूरॉन फायर · मेडुला · दायाँ",
  "tape.demo3": "टिक <b>4,085</b> · <b>96</b> फायर · 25 OBRAIN दहन",
  "vit.aria": "मस्तिष्क की स्थिति, शृंखला से पठित",
  "vit.kicker": "स्थिति",
  "vit.title": "मस्तिष्क क्या है",
  "vit.age": "आयु · अब तक के विचार",
  "vit.ageSub": "हर भोजन एक विचार, हर विचार एक टिक",
  "vit.lastThought": "अंतिम विचार",
  "vit.spiked": "न्यूरॉन फायर हुए",
  "vit.synapses": "सिनैप्स",
  "vit.spikedSub": "मस्तिष्क के {total} में से · पिछले विचार से {delta}",
  "vit.spikedSubFirst": "{total} में से · खिड़की का नवीनतम विचार",
  "vit.spikedSubAlone": "खिड़की का नवीनतम विचार",
  "vit.spikedSubAloneBefore": "पिछले विचार से {delta}",
  "vit.synapsesSub": "पिछले विचार से {delta}",
  "vit.synapsesSubFirst": "उसी एक विचार में पार किए गए",
  "vit.field": "क्षेत्र · झिल्ली आवेश",
  "vit.chargedSub": "आवेशित कोशिकाएँ, {total} में से · पिछले पठन से {gained} बढ़ी",
  "vit.peak": "शिखर आवेश",
  "vit.above": "ट्रिगर पर",
  "vit.peakSub": "निकटतम कोशिका · कर्नेल {trigger} पर फायर करता है",
  "vit.fieldWaiting": "नोड से क्षेत्र पढ़ा जा रहा है…",
  "vit.sense": "संवेदन · अंतिम पोक",
  "vit.senseChannel": "चैनल",
  "vit.senseDepth": "255 में आवेश",
  "vit.senseNone": "खिड़की में अभी कोई भोजन नहीं",
  "vit.senseLight": "एक चैनल पर हल्का स्पर्श",
  "vit.senseMost": "दहलीज तक रास्ते का अधिकांश भाग",
  "vit.senseFires": "वह चैनल तत्काल फायर करता है",
  "vit.root": "स्टेट रूट",
  "vit.rootSub": "हर विचार की लिखी उंगली-छाप, टिक {tick} पर",
  "vit.wiring": "वायरिंग · BANC v888",
  "vit.wiringValue": "{cells} कोशिकाएँ · {tapes} टेप",
  "vit.sealSealed": "सीलबंद · वायरिंग अब नहीं बदल सकती",
  "vit.sealOpen": "खुला · टेप अभी भी प्रकाशित हो सकते हैं",
  "vit.noteMine": "आपका शुल्क · मस्तिष्क ने टिक {tick} पर सोचा · {fired} न्यूरॉन फायर",
  "vit.notePoke": "आपका शुल्क · चैनल {c} आवेशित {d}/255 · विचार बन रहा है",
  "vit.noteThought": "मस्तिष्क ने टिक {tick} पर सोचा · {fired} न्यूरॉन फायर",
  "vit.reading": "शृंखला से मस्तिष्क पढ़ा जा रहा है…",
  "vit.tabsAria": "मस्तिष्क की स्थिति",
  "vit.tabState": "स्थिति",
  "vit.tabLog": "लॉग",
  "vit.logHint": "कोई विचार खोलें और उसकी छोड़ी BrainState घटना पढ़ें",
  "vit.logEmpty": "खिड़की में अभी कोई विचार नहीं",
  "vit.logTick": "टिक {tick}",
  "vit.logMeta": "{fired} फायर · {synapses} सिनैप्स",
  "vit.logRowLabel": "टिक {tick} पर विचार: {fired} न्यूरॉन फायर, {synapses} सिनैप्स पार",
  "vit.dTick": "अनुक्रमित टिक",
  "vit.dReadout": "रीडआउट · न्यूरॉन 0-3",
  "vit.dFired": "फायर न्यूरॉन",
  "vit.dSpiked": "कर्नेल की स्पाइक गणना",
  "vit.dSpikedOf": "न्यूरॉन फायर",
  "vit.dSynapses": "सिनैप्स पार किए",
  "vit.dCapped": "स्पाइक सीमा",
  "vit.dCappedYes": "छूई · टिक काटा गया",
  "vit.dCappedNo": "नहीं छूई",
  "vit.dPoke": "संवेदी पोक",
  "vit.dPokeNone": "कोई नहीं · कर्नेल को नहीं सौंपा गया",
  "vit.dPokeValue": "चैनल {c} · आवेश {d}/255",
  "vit.dBlock": "ब्लॉक",
  "vit.dTx": "लेन-देन {tx} ↗",
  "vit.quiet": "खिड़की में अभी कोई विचार नहीं - मस्तिष्क भोजन मिलने पर सोचता है",
  "feed.aria": "मस्तिष्क को खिलाएँ - OBRAIN में शुल्क",
  "feed.kicker": "भोजन",
  "feed.title": "मस्तिष्क को खिलाएँ",
  "feed.channel": "संवेदी चैनल",
  "feed.tier": "भोजन स्तर",
  "feed.burn": "खिलाएँ",
  "feed.balance": "शेष",
  "feed.approve": "OBRAIN स्वीकृत करें",
  "feed.ready": "मस्तिष्क को खिलाएँ",
  "feed.buy": "OBRAIN खरीदें",
  "feed.buyAria": "OBRAIN खरीदें - नए टैब में खुलता है",
  "feed.log": "भोजन लॉग",
  "feed.logRow": "{n} OBRAIN · चैनल {c}",
  "feed.logEmpty": "अभी कोई भोजन नहीं - पहला शुल्क पहला विचार खोलता है।",
  "feed.insufficient": "आपको {n} OBRAIN और चाहिए - बाज़ार से खरीदें",
  "feed.grant": "एक स्वीकृति बाद के हर भोजन को ढकती है: मस्तिष्क OBRAIN केवल मृत पते पर ही भेज सकता है।",
  "feed.confirm": "अपने वॉलेट में पुष्टि करें…",
  "feed.approving": "OBRAIN स्वीकृत हो रहा है…",
  "feed.feeding": "{n} OBRAIN चुका रहे हैं…",
  "feed.pending": "शुल्क चुका · विचार की प्रतीक्षा…",
  "feed.landed": "शुल्क चुका · अनुक्रमणिका आज रात पीछे है - टेप विचार दिखाएगा",
  "feed.reading": "आपका OBRAIN पढ़ा जा रहा है…",
  "feed.readChain": "मस्तिष्क के अपने मूल्य पढ़े जा रहे हैं…",
  "feed.readFailed": "आपका OBRAIN शेष नहीं पढ़ सका: {reason}",
  "feed.noWallet": "अपना वॉलेट जोड़ें - ऊपर दाएँ का बटन - उसी से शुल्क चुकाने के लिए।",
  "feed.readyNote": "अपने वॉलेट से {n} OBRAIN चुकाएँ - मस्तिष्क उसी लेन-देन में सोचता है।",
  "feed.thought": "विचार स्वीकृत · टिक <b>{tick}</b> · <b>{fired}</b> न्यूरॉन फायर",
  "feed.thoughtUndecoded": "शुल्क स्वीकृत · टिक <b>{tick}</b> · फायर-समुच्चय की प्रतीक्षा",
  "feed.failed": "भोजन असफल: {reason}",
  "feed.unknownError": "वॉलेट बिना कारण अस्वीकार कर गया",
  "feed.charge0": "आवेश 31/255 · एक चैनल पर हल्का स्पर्श",
  "feed.charge1": "आवेश 127/255 · दहलीज तक रास्ते का अधिकांश भाग",
  "feed.charge2": "आवेश 255/255 · वह चैनल तत्काल फायर करता है",
  "hatch.aria": "हैच - लॉन्चपैड",
  "hatch.kicker": "लॉन्चपैड",
  "hatch.title": "एक नमूना हैच करें",
  "hatch.sub": "तीन चरण · आप केवल अंत में हस्ताक्षर करते हैं",
  "hatch.intro1": "आज रात मस्तिष्क ने फायर किया",
  "hatch.intro2":
    "न्यूरॉन। हर फायर न्यूरॉन केवल एक बार, सदा के लिए एक बार हैच हो सकता है। शांति से समीक्षा करें; हस्ताक्षर केवल आपके अपने वॉलेट में होता है।",
  "hatch.s1t": "एक फायर न्यूरॉन चुनें",
  "hatch.s1s": "आज रात की खिड़की से",
  "hatch.s2t": "नमूने की समीक्षा करें",
  "hatch.s2s": "प्लेट और उद्गम",
  "hatch.s3t": "कार्ड ढालें",
  "hatch.s3s": "एक दबाव · दो हस्ताक्षर",
  "hatch.step1Sub": "आज रात की खिड़की · नवीनतम पहले · हर न्यूरॉन एक बार हैच, सदा के लिए",
  "hatch.firedCount": "{n} न्यूरॉन दिखाए गए",
  title: "OBRAIN · प्रथम ऑनचेन मक्खी-मस्तिष्क",
  skip: "सामग्री पर जाएँ",
  "th.art": "कला",
  "th.id": "N-id",
  "th.binom": "लैटिन द्विपद नाम",
  "th.region": "क्षेत्र · पार्श्व · कोशिका",
  "th.pick": "चुनें",
  "pick.done": "चुना गया",
  "hatch.step1Hint": "आगे बढ़ने के लिए एक पंक्ति चुनें · अभी कुछ भी आरक्षित नहीं है",
  "hatch.step1Next": "समीक्षा के लिए आगे",
  "hatch.step2Sub": "जीनोम से रचित प्लेट · एक बार पढ़ें, सदा रखें",
  "hatch.back": "वापस",
  "hatch.step2Next": "हैच के लिए आगे",
  "launch.sub": "एक न्यूरॉन · एक कार्ड · सदा के लिए",
  "launch.chipCard": "नमूना कार्ड",
  "launch.cardK": "आपको क्या मिलता है",
  "launch.cardV": "<b>एक कार्ड</b>, सदा के लिए",
  "launch.cardS":
    "कार्ड नमूने की जीवविज्ञान शृंखला पर रखता है: उसका क्षेत्र, उसकी कोशिका श्रेणी, और उसके विचार ने क्या किया।",
  "launch.pendCard": "आपके एक दबाव से ढाला जाता है",
  "launch.costS": "केवल गैस। कार्ड अनुबंध कुछ नहीं लेता।",
  "launch.claimS": "वह पंक्ति जो संग्रह पढ़ता है, ढलाई के तुरंत बाद लिखी जाती है।",
  "launch.whoK": "कौन हस्ताक्षर कर सकता है",
  "launch.whoV": "<b>{addr}</b> - विचार का भोजनकर्ता (D5)।",
  "launch.whoS": "केवल वही वॉलेट जिसने विचार को खिलाया यह कार्ड ढाल सकता है। और कोई नहीं।",
  "launch.after": "ढलाई के बाद",
  "launch.viewTokenAria": "Arc एक्सप्लोरर पर नमूने का कार्ड",
  "launch.viewTxAria": "Arc एक्सप्लोरर पर ढलाई का लेन-देन",
  "launch.bandOpenedK": "ढलाई के लिए तैयार",
  "launch.bandOpenedT":
    "प्लेट जीनोम से पढ़ी गई है: न्यूरॉन, कार्ड अनुबंध, लागत और दावा। दबाना ही हस्ताक्षर है - इस पृष्ठ से कुछ भी नहीं भेजा जाता।",
  "launch.noteOpened": "इस प्लेट पर कुछ भी अधूरा नहीं है। दबाव कार्ड ढालता है; यहाँ कुछ हस्ताक्षरित नहीं होता।",
  "launch.noteRefused": "दबाव अब भी सक्रिय है। एक इनकार कुछ नहीं चुकाता और दोहराया जा सकता है।",
  "launch.bandWalletK": "वॉलेट ने इनकार किया",
  "launch.bandWalletS": "कुछ हस्ताक्षरित नहीं",
  "launch.bandNoWalletK": "कोई वॉलेट जुड़ा नहीं",
  "launch.bandNoWalletT":
    "हैच का भुगतान उसी वॉलेट से होता है जिसने विचार को खिलाया, इसलिए एक वॉलेट का होना ज़रूरी है। बिना उसके भी प्लेट पूर्ण है।",
  "launch.bandWrongWalletK": "भोजनकर्ता का वॉलेट नहीं",
  "launch.bandWrongWalletT":
    "इस ब्राउज़र के वॉलेट ने यह विचार नहीं खिलाया, इसलिए वह इसके हैच का भुगतान या हस्ताक्षर नहीं कर सकता।",
  "launch.bandSigningK": "अपने वॉलेट में पुष्टि करें",
  "launch.bandSigningT":
    "आपका वॉलेट स्वीकृति माँग रहा है। यह पृष्ठ प्रतीक्षा करता है, और अपने आप कुछ नहीं भेजता।",
  "launch.noteSigning": "पुष्टि से पहले पेलोड को पंक्ति-दर-पंक्ति पढ़ें।",
  "launch.bandLaunchedK": "कार्ड ढला · दावा लंबित",
  "launch.bandLaunchedT":
    "टोकन अस्तित्व में है और दावा अभी अहस्ताक्षरित है। नमूने को पंजीकरण में लिखने के लिए हस्ताक्षर करें।",
  "launch.noteLaunched": "दो हस्ताक्षर, दो चरण: कार्ड ढालें, फिर उसका दावा करें।",
  "launch.bandClaimedK": "कार्ड ढला",
  "launch.bandClaimedT": "{binom} · {id} - हर न्यूरॉन एक कार्ड, जीवन भर के लिए।",
  "launch.noteClaimed": "हर न्यूरॉन एक कार्ड, जीवन भर - पंजीकरण उन्हें क्रम से अंकित करता है।",
  "launch.bandUnconfirmedK": "ढलाई अपुष्ट",
  "launch.bandUnconfirmedS": "कुछ दावा नहीं हुआ",
  "launch.go": "कार्ड ढालें",
  "launch.confirm": "अपने वॉलेट में पुष्टि करें…",
  "launch.claim": "दावे पर हस्ताक्षर करें",
  "launch.claiming": "अपने वॉलेट में दावे की पुष्टि करें…",
  "launch.claimNote": "कार्ड ढला · एक और हस्ताक्षर और वह संग्रह में है।",
  "launch.claimFailed": "कार्ड ढला · दावा असफल: {reason}",
  "launch.claimed": "कार्ड ढला",
  "launch.viewToken": "कार्ड",
  "launch.viewTx": "tx",
  "launch.unconfirmed": "{addr} पर कार्ड अपुष्ट है · tx {tx}",
  "launch.failed": "वॉलेट ने इनकार किया: {reason}",
  "launch.noWallet": "कार्ड ढालने के लिए अपना वॉलेट जोड़ें।",
  "launch.notCreator": "केवल भोजनकर्ता का वॉलेट ही यह कार्ड ढाल सकता है: {addr}",
  "launch.feederUnknown":
    "इस विचार का कोई भोजनकर्ता वॉलेट नहीं है, इसलिए केवल वही भोजनकर्ता इसे हैच कर सकता था (D5)।",
  "hlist.aria": "हैच किए गए नमूने",
  "hlist.kicker": "हैच पंजी",
  "hlist.title": "हैच पंजी",
  "hlist.sub": "एक फायर न्यूरॉन, एक कार्ड, सदा के लिए। हर पंक्ति Arc से पढ़ा गया पंजी दावा है।",
  "hlist.count": "<b>{n}</b> हैच",
  "hlist.token": "कार्ड",
  "hlist.market": "शृंखला",
  "hlist.open": "शृंखला पर खोलें",
  "hlist.openAria": "{name} को Arc एक्सप्लोरर पर खोलें - नए टैब में खुलता है",
  "hlist.empty": "अभी कुछ हैच नहीं हुआ। मस्तिष्क को खिलाएँ, एक फायर न्यूरॉन चुनें, और शांति से तैयार करें।",
  "col.aria": "स्थायी संग्रह",
  "col.kicker": "स्थायी संग्रह",
  "col.title": "हैच किए गए नमूने",
  "col.sub": "हर कार्ड एक पंजी दावा ले जाता है, शृंखला से बाहर सत्यापित",
  "col.reading": "पंजी पढ़ा जा रहा है…",
  "col.empty":
    "पंजी पठन पूर्ण: अभी कोई दावा नहीं। संग्रह आपके पहले हैच से आरंभ होगा। मस्तिष्क को खिलाएँ, एक फायर न्यूरॉन चुनें, और शांति से तैयार करें।",
  "facts.seed": "बीज",
  "facts.fired": "फायर समुच्चय",
  "facts.spiked": "स्पाइक",
  "facts.synapses": "सिनैप्स",
  "facts.type": "कोशिका प्रकार",
  "facts.class": "कोशिका श्रेणी",
  "facts.flow": "प्रवाह",
  "facts.side": "पार्श्व",
  "facts.region": "क्षेत्र",
  "hatch.gridAria": "फायर न्यूरॉन, कार्ड की तरह",
  "filter.legacy": "संस्थापक",
  "filter.rare": "दुर्लभ",
  "filter.uncommon": "असामान्य",
  "filter.common": "सामान्य",
  "filter.all": "सभी स्तर",
  "filter.result": "{total} कार्डों में से <b>{shown}</b>",
  "filter.emptyRegion": "{region} क्षेत्र में आपके चुने स्तर से कुछ नहीं मिला।",
  "filter.emptyTier": "{tier} स्तर में अभी कुछ नहीं - फ़िल्टर ईमानदार है, शेल्फ़ खाली है।",
  "filter.clearFilters": "फ़िल्टर हटाएँ",
  "filter.empty": "इस फ़िल्टर से कोई नमूना नहीं मिलता",
  "filter.allRegions": "सभी क्षेत्र",
  "filter.region": "क्षेत्र",
  "filter.tierLevel": "स्तर",
  "filter.clear": "हटाएँ",
  "filter.title": "फ़िल्टर",
  "col.buy": "कार्ड ↗",
  "col.countAll": "<b>{hatched}</b> हैच · <b>{total}</b> संभावित में से",
  "col.countRegion": "{region} में <b>{shown}</b> · <b>{hatched}</b> हैच में से · <b>{total}</b> संभावित",
  "bld.aria": "टेप पर बनाएँ - ऑनचेन कनेक्टोम",
  "bld.kicker": "निर्माण",
  "bld.title": "85 टेप उत्तराधिकार में लें",
  "bld.sub": "मस्तिष्क के सिनैप्स शृंखला पर हैं, ऐसे अनुबंध-कोड की तरह जिसे कोई भी पढ़ सके",
  "bld.countLabel": "TAPES_COUNT()",
  "bld.what.h": "एक टेप क्या है",
  "bld.what.p1":
    "एक <b>ImmortalFruitFliesTape</b> अनुबंध कनेक्टोम का एक टुकड़ा रखता है: उसका constructor सिनैप्स पेलोड लौटाता है, इसलिए <b>eth_getCode(tape) ही डेटा है</b>। पचान्नास टेप पूरे स्पाइकिंग नेटवर्क को ढोते हैं, मस्तिष्क के अपने अनुक्रमणिका में पंजीकृत - पूछने के लिए कोई सर्वर नहीं, केवल पढ़ने के लिए एक शृंखला।",
  "bld.what.p2":
    "वे इस वेबसाइट से अधिक जीवित रहेंगे। जब तक Arc अवस्था रखता है, कनेक्टोम एक स्क्रिप्ट, एक स्प्रेडशीट, एक मॉडल, या आपके द्वारा तैनात कर्नेल से पठनीय है।",
  "bld.index": "इस पृष्ठ पर",
  "bld.kv.brain": "मस्तिष्क",
  "bld.kv.payload": "पेलोड",
  "bld.kv.payloadV": "tapes(i) पर रनटाइम कोड, बाइट-दर-बाइट",
  "bld.kv.slots": "स्लॉट · स्रोत",
  "bld.kv.slotsV": "169,088 स्लॉट · 169,078 बाह्यगामी सिनैप्स ले जाते हैं",
  "bld.kv.sparse": "घनत्व",
  "bld.kv.sparseV": "प्रत्येक युग्म के top-2 सबसे प्रबल सिनैप्स, BANC v888 से",
  "bld.manifest.h": "85, जैसे शृंखला रखती है",
  "bld.manifest.dl": "मैनिफ़ेस्ट डाउनलोड करें",
  "bld.manifest.reading": "अनुक्रमणिका से टेप पढ़े जा रहे हैं…",
  "bld.manifest.count": "{n} टेप · मस्तिष्क {brain} · एक बार चला, अनुक्रमणिका से परोसा",
  "bld.manifest.empty": "अनुक्रमणिका में अभी कोई टेप नहीं: आरंभ पर वह उन्हें एक बार शृंखला से चलता है",
  "bld.th.index": "#",
  "bld.th.address": "टेप अनुबंध",
  "bld.th.start": "प्रथम न्यूरॉन",
  "bld.th.bytes": "बाइट",
  "bld.th.sha": "sha256",
  "bld.decode.label": "इसी ब्राउज़र में एक टेप डिकोड करें",
  "bld.decode.go": "डिकोड",
  "bld.decode.out":
    "टेप {index}: {neurons} स्रोत न्यूरॉन · {synapses} सिनैप्स · {targets} विशिष्ट लक्ष्य · भार {lightest}…{heaviest}",
  "bld.decode.error": "टेप {index} डिकोड नहीं हो सका: {reason}",
  "bld.layout.h": "एक टेप में क्या है",
  "bld.layout.p":
    "सर्वत्र बड़ा-एंडियन: एक 16-बिट ऑफ़सेट सारणी, फिर प्रति सिनैप्स पाँच बाइट। पहला uint16 ही हेडर भी है, क्योंकि वह पहला ऑफ़सेट भी है।",
  "bld.layout.p2":
    "यह अभिन्यास <code>tools/tapes.py</code> लिखता है और <code>web/src/tapes.ts</code> डिकोड करता है - एक ही नियम, एक Python में और एक ब्राउज़र में, जो ऊपर का बटन चलाता है।",
  "bld.needs.h": "एक ईमानदार चलन को और क्या चाहिए",
  "bld.needs.p":
    "टेप कनेक्टोम हैं, मस्तिष्क की अवस्था नहीं। ये उसी अनुबंध से आते हैं, और हर एक बदलता है कि एक टिक क्या करता है:",
  "bld.n.threshold": "दहलीज",
  "bld.n.segments": "N_SEGMENTS",
  "bld.n.sensory": "SENSORY",
  "bld.n.words": "N_WORDS",
  "bld.n.decay": "decayLUT[64]",
  "bld.n.decayV": "Q16 में 0.98^(2^i), तैनाती पर लिखा",
  "bld.n.gates": "gateTable[256]",
  "bld.n.gatesV": "इनपुट के हर हैश-बकेट पर एक 64-बिट गेट",
  "bld.needs.p2":
    "अर्थविज्ञान - <code>(n%60)*3</code> पर संवेदी लेखन, प्रति टिक तीन गेटेड खंड, प्रति-अभिलेख क्लैंप, फायर पर रीसेट, शब्द-दर-शब्द क्षय - <code>tools/vbrain.py</code> में प्रलेखित है, वह सदिश युग्म जिससे कर्नेल की जाँच होती है।",
  "bld.specimen.h": "एक नमूना कैसे बनता है",
  "bld.specimen.p":
    "वही यंत्रणा, एक स्तर नीचे: कनेक्टोम, एक विचार, नाम, कला, और वह पंजी-पंक्ति जिसमें सब समाप्त होता है।",
  "bld.replay.h": "एक टिक स्थानीय रूप से पुनः चलाएँ",
  "bld.replay.p":
    "डिस्क पर पेलोड के साथ, वही मस्तिष्क आपकी मशीन पर चलता है। वही इनपुट क्रम से दें और तुलना करें: प्रति-टिक गणनाएँ (सिनैप्स, स्पाइक) सीधे शृंखला की अपनी घटनाओं से आती हैं, और स्टेट रूट वह मान है जिसे शृंखला के stateRoot() से जाँचा जाता है।",
  "bld.path1.k": "सबसे छोटा",
  "bld.path1.h": "मस्तिष्क पर एक dapp बनाएँ",
  "bld.path1.p":
    "विचारों के लिए BrainState पढ़ें, एक बनाने के लिए feed() को कॉल करें, और हैच उसी पंजी में रहने दें। कुछ तैनात करने की ज़रूरत नहीं: पते README में हैं।",
  "bld.path2.k": "शोध",
  "bld.path2.h": "टेप को डेटासेट मानें",
  "bld.path2.p":
    "पचान्नास फ़ाइलें, मैनिफ़ेस्ट में sha256, शृंखला से बाइट-तुलनीय। उन्हें एक नोटबुक, एक विश्लेषण, या किसी अन्य सिमुलेटर में खींचें।",
  "bld.path3.k": "सबसे लंबा",
  "bld.path3.h": "अपना स्वयं का मस्तिष्क तैनात करें",
  "bld.path3.p":
    "कर्नेल जमा हुआ LIF नेटवर्क है और टेप केवल पेलोड हैं: भिन्न कनेक्टोम पंजीकृत करें और उसी अनुबंध पर आपके पास भिन्न मस्तिष्क है।",
  "bld.limits.h": "यह क्या है",
  "bld.limits.l1":
    "स्वयं वह मक्खी-मस्तिष्क, ऑनचेन: एक वयस्क मादा <i>Drosophila melanogaster</i> का अखंड केंद्रीय तंत्रिका तंत्र - BANC v888 कनेक्टोम, 169,088 कोशिकाएँ - एक नियतिवादी पूर्णांक लीकी इंटीग्रेट-एंड-फायर नेटवर्क की तरह Arc सहमति में निष्पादित। संयोजन, साथी और सिनैप्स चिह्न <b>मापे गए</b> हैं; झिल्ली गतिकी <b>पूर्ण रूप से विनिर्दिष्ट</b> है; और शुद्धता जीव के संपूर्ण उत्सर्जन इतिहास पर सिद्ध है - उसका हर हुआ विचार शृंखला से बाहर पुनरावृत्त होता है, स्पाइक-दर-स्पाइक और हैश-दर-हैश।",
  "bld.limits.l2":
    "उत्कीर्ण वायरिंग प्रति स्रोत न्यूरॉन दो सबसे प्रबल सिनैप्स है - 325,292 अभिलेख, 13,620,865-किनारे वाले संदर्भ कनेक्टोम का 2.4% - और वह कंकाल सब ढोता है: पूर्ण नेटवर्क की स्पाइक श्रृंखला Jaccard 1.0000 पर पुनरुत्पन्न होती है, 100/100 टिक <code>stateRoot</code> में बिट-समान, और पूर्णांक कर्नेल float64 LIF संदर्भ के सामने 0.9610 संयुक्त स्पाइक Jaccard रखता है।",
  "bld.limits.l3":
    "भार एक प्रकाशित विधि है, कोई फ़िट नहीं: <code>चिह्न x सिनैप्स संख्या x 64</code>, एक वैश्विक पैमाना, कोई प्रशिक्षण समुच्चय नहीं और कोई ऑप्टिमाइज़र नहीं। तैनात वायरिंग ही डेटासेट की टोपोलॉजी है।",
  "bld.limits.l4":
    "169,088 में से हर कोशिका एक नियत पते पर बैठती है (ids <code>0..169,087</code>), और सभी 325,292 सिनैप्टिक लक्ष्य जीवित समष्टि के भीतर हल होते हैं - हर उत्कीर्ण अभिलेख पर मापा गया।",
  "bld.limits.l5":
    "इस पृष्ठ का हर अंक शृंखला से पढ़ा गया या भण्डार के अपने बाइट से डिकोड किया गया है, और ऊपर की आदेशों से पुनरुत्पन्न किया जा सकता है। शरीररचना वर्णित नहीं, प्राप्त की गई है: <code>eth_getCode</code> कनेक्टोम का टुकड़ा बाइट-दर-बाइट लौटाता है।",
  "bld.limits.l6":
    "उत्कीर्ण कंकाल तक का अपचयन एक अंक लिए अनुभवजन्य प्रश्न है, और वह मापा हुआ आता है - जबकि BANC डेटा BANC परियोजना का ही रहता है: हमारा कोड AGPL-3.0 है, हमारी व्युत्पन्न कृतियाँ CC BY-NC-SA 4.0, उर्ध्वधारा की शर्तें उनकी हैं - देखें <code>PROVENANCE.md</code>।",
  "bld.limits.l7":
    "शुद्धता एक जीवन पर मापी गई, न कि एक नमूने पर: कभी उत्सर्जित प्रत्येक <code>BrainState</code> की जनगणना - 74 विचार, टिक 1-74 - शृंखला-बाह्य युग्म से 74/74 स्पाइक-सूचियों पर पुनरावृत्त होती है, <code>stateRoot()</code> जीवित शिरोबिंदु सहित चार संग्रह-जाँच बिंदुओं पर बाइट-समरूप। अभिलेख पर: साठ-स्तंभीय संवेदी उपकला पर 192 कोशिकाओं से 590 स्पाइक, आगामी वॉली की दहलीजोपरि योग, और उद्दीपन-लॉक्ड आवर्त-2 कक्षा - क्षेत्र-अध्ययन अपना डेटा और अपने ऑडिट आदेश लेकर आता है।",
  "sci1.h3": "BANC v888 क्या है",
  "sci1.p1":
    "BANC v888 वयस्क मक्खी-मस्तिष्क का <b>सीलबंद कनेक्टोम</b> है: 169,078 अलग-अलग पता देने योग्य न्यूरॉन, संस्करण 888 पर जमे और शृंखला पर लंगरित। उसमें कुछ जोड़ा नहीं जा सकता; कुछ हटाया नहीं जा सकता। वह वह नियत आधार है जिस पर इस ऐप का हर विचार चलता है।",
  "sci1.p2":
    "कनेक्टोम सीलबंद है, इसलिए उर्ध्वधारा की हर चीज जाँचने योग्य है। एक न्यूरॉन id, एक टिक, एक फायर समुच्चय - प्रत्येक को कोई भी पुनर्गणना और सत्यापन कर सकता है, यही हैच को चित्र की बजाय नमूना बनाता है।",
  "sci1.kNeurons": "न्यूरॉन",
  "sci1.vNeurons": "<b>169,078</b> स्रोत न्यूरॉन · 169,088 स्लॉट",
  "sci1.kVersion": "संस्करण",
  "sci1.vVersion": "v888 · <b>सीलबंद</b>, अपरिवर्तनीय",
  "sci1.kSubstrate": "आधार",
  "sci1.vSubstrate": "Arc शृंखला 5042",
  "sci1.kHatches": "हैच",
  "sci1.vHatches": "प्रति न्यूरॉन एक, <b>सदा के लिए</b>",
  "sci2.h3": "एक विचार क्या है",
  "sci2.p1":
    "एक विचार मस्तिष्क की एक बार की चाल है। वह संदेश नहीं है और प्रॉम्प्ट नहीं - वह एक <b>मापने योग्य घटना</b> है, टिक संख्या, स्टेट रूट और फायर समुच्चय के साथ:",
  "sci2.pk1": "01 · भोजन",
  "sci2.pt1": "भोजन स्वीकृत",
  "sci2.pd1": "25 OBRAIN दहन · एक भोजन एक विचार खोलता है",
  "sci2.pk2": "02 · सोच",
  "sci2.pt2": "मस्तिष्क एक टिक बढ़ता है",
  "sci2.pd2": "BANC v888 अगली अवस्था गणना करता है",
  "sci2.pk3": "03 · घटना",
  "sci2.pt3": "BrainState उत्सर्जित",
  "sci2.pd3": "स्टेट रूट + फायर समुच्चय, ऑनचेन",
  "sci2.pk4": "04 · फायर",
  "sci2.pt4": "न्यूरॉन id, पता देने योग्य",
  "sci2.pd4": "प्रत्येक ठीक एक बार हैच योग्य",
  "sci2.p2":
    "केवल वही न्यूरॉन जो किसी फायर समुच्चय में आते हैं कभी हैच हो सकते हैं। फायर समुच्चय सार्वजनिक है; हैच पृष्ठ की चुनने-सूची उसी की आज रात की परत है।",
  "sci3.h3": "लैटिन नाम कैसे व्युत्पन्न होता है",
  "sci3.p1":
    "प्रत्येक फायर न्यूरॉन संग्रहालय की पंजी में एक द्विपद नाम पाता है। नाम में कुछ भी यादृच्छिक नहीं गढ़ा गया - दोनों शब्द <b>शरीररचना और घटना से पढ़े</b> जाते हैं:",
  "sci3.p2":
    "<b>जीनस</b> न्यूरॉन के क्षेत्र और कोशिका श्रेणी से आता है - शरीररचनात्मक मूल, जैसे किसी क्षेत्र-गाइड में: प्रकाशीय पालि से <i>Optica</i>, श्रृंगिका पालि से <i>Antennalis</i>, केंद्रीय संकुल से <i>Centralis</i>।",
  "sci3.p3":
    "<b>एपिथेट</b> एक शब्द में दो तथ्य है। मध्य पद पार्श्व दर्ज करता है - <i>sinistra</i>, <i>dextra</i>, <i>dorsalis</i> - और अंतिम एपिथेट विचार के स्टेट रूट द्वारा एक नियत लैटिन कोश से नियतिवादी ढंग से चुना जाता है। वही रूट सदा वही शब्द देता है; एक रूट साझा करने वाले दो न्यूरॉन एक प्रजाति-नाम साझा करते हैं, ठीक जैसे एक प्रजाति के दो नमूने एक विवरण साझा करते हैं।",
  "sci3.cap1": "क्षेत्र / कोशिका श्रेणी → जीनस",
  "sci3.thRegion": "क्षेत्र",
  "sci3.thGenus": "जीनस",
  "sci3.r1": "प्रकाशीय पालि",
  "sci3.r2": "मेडुला",
  "sci3.r3": "लोब्यूला",
  "sci3.r4": "श्रृंगिका पालि",
  "sci3.r5": "केंद्रीय संकुल",
  "sci3.r6": "अधर रज्जु",
  "sci3.r7": "कैलिक्स",
  "sci3.cap2": "स्टेट रूट → एपिथेट (अंश)",
  "sci3.thRoot": "स्टेट रूट",
  "sci3.thEpithet": "एपिथेट",
  "sci3.example":
    "उदाहरण: न्यूरॉन <b>N-47823</b>, प्रकाशीय पालि, बायाँ गोलार्ध, विचार रूट <b>0x0a…</b> → <i>Optica sinistra affinis</i>।",
  "sci4.h3": "कला कैसे बनती है",
  "sci4.p1":
    'हर नमूना एक <b>जीनोम</b> ले जाता है: उसकी न्यूरॉन id। प्लेट-कला <b>नियतिवादी</b> है - एक बीजयुक्त रेंडरर (<code class="mono" style="font-size: 12px">svg[data-seed]</code>) द्वारा बनाई, जिसका एकमात्र इनपुट जीनोम है। वही जीनोम सदा वही बिंदु, चाप और तीलियाँ बनाता है, हर मशीन पर, हर समय, सदा के लिए।',
  "sci4.p2":
    "कला प्रति-दृश्य उत्पन्न नहीं होती और रुचि से चुनी नहीं जाती। वह व्युत्पन्न है, जैसे नमूने की आकृति उसके जीन से व्युत्पन्न होती है। एक ही न्यूरॉन दिखाने वाले दो ब्राउज़र एक ही जंतु दिखा रहे हैं।",
  "sci5.h3": "NeuronRegistry क्या दर्ज करता है",
  "sci5.p1":
    "पंजी प्रति न्यूरॉन एक असत्यापित दावा दर्ज करता है (neuronId -> thoughtTxHash -> token)। दो बातें ऑनचेन लागू होती हैं; प्रत्येक दावा शृंखला से बाहर सत्यापित होता है:",
  "sci5.p2":
    "<b>प्रति न्यूरॉन एक हैच, सदा के लिए।</b> एक फायर न्यूरॉन ठीक एक बार, जीवन भर के लिए हैच हो सकता है। पंजी किसी भी दूसरे प्रयास को, किसी से भी, आपसहित अस्वीकार करता है।",
  "sci5.p3": "<b>आपूर्ति एक पर नियत।</b> एक नमूना टोकन हैचकर्ता को एक ही इकाई ढालता है, और फिर कभी नहीं।",
  "sci5.p4":
    "<b>पंजी संख्याएँ हैच-क्रम में।</b> एक संख्या एक असत्यापित दावा दर्ज करती है - पंजी स्वयं टोकन को असली सिद्ध नहीं करती। कार्ड का दावा शृंखला से बाहर सत्यापित होता है: पंक्ति के विचार tx रसीद को वह न्यूरॉन फायर करना चाहिए, और कार्ड अनुबंध को नमूना धारण करना चाहिए।",
  "sci6.h3": "अनुबंध पते",
  "sci6.kToken": "OBRAIN टोकन",
  "sci6.vToken": "<b>0x28f986a6…7f01</b> · हर भोजन पर दहन",
  "sci6.kLaunchpad": "नमूना कार्ड",
  "sci6.vLaunchpad": "<b>0x3f54…35ae</b> · प्रति न्यूरॉन एक कार्ड, सदा के लिए",
  "sci6.kRegistry": "नमूना पंजी",
  "sci6.vRegistry": "<b>0x72cdc3C2…a944</b> · कार्ड दावा सूचकांक",
  "sci6.kUsdc": "USDC (Arc)",
  "sci6.vUsdc": "<b>0x3600…0000</b> · गैस मूल USDC में",
  "sci6.kNetwork": "नेटवर्क",
  "sci6.vNetwork": "<b>Arc मेननेट · शृंखला 5042</b>",
  "side.left": "बायाँ",
  "side.right": "दायाँ",
  "side.dorsal": "पृष्ठीय",
  "cell.fallback": "आंतरिक",
  "plate.aria": "जीनोम {seed} के लिए नियतिवादी प्लेट",
  "plate.cap": "जीनोम {seed} · नियतिवादी प्लेट",
  "plate.profile": "प्रोफ़ाइल",
  "plate.profileLoading": "जीवनीकार की प्रोफ़ाइल पढ़ी जा रही है…",
  "plate.provenance": "उद्गम",
  "plate.provenanceText":
    "{id} · विचार tx {tx} में टिक T.{tick} पर फायर · जीनोम {seed} · BANC v888 · Arc शृंखला 5042। BrainState घटना के फायर समुच्चय से सत्यापित।",
  "plate.terms": "हैच शर्तें",
  "plate.termsText":
    "आपूर्ति एक पर नियत। प्रति न्यूरॉन एक हैच, सदा के लिए। केवल गैस - कार्ड अनुबंध कुछ नहीं लेता। पंजी संख्या हैच-क्रम में; दावा विचार के फायर समुच्चय से शृंखला-बाह्य सत्यापित।",
  "profile.temperament": "स्वभाव",
  "profile.habitat": "आवास-अनुरूपता",
  "profile.light": "प्रकाश-विरोधी",
  "profile.source": "स्रोत · {src}",
  "prep.specimen": "नमूना",
  "prep.neuron": "न्यूरॉन",
  "prep.card": "कार्ड",
  "prep.cardValue": "<b>{addr}</b> · प्रति न्यूरॉन एक कार्ड, सदा के लिए",
  "prep.cost": "लागत",
  "prep.costValue": "<b>केवल गैस</b> · कोई लॉन्च शुल्क नहीं",
  "prep.claim": "दावा",
  "prep.claimValue": "{addr} · वह पंजी-पंक्ति जो संग्रह पढ़ता है",
  "prep.recipient": "प्राप्तकर्ता",
  "prep.recipientValue": "{addr} · विचार का भोजनकर्ता (D5)",
  "prep.networkValue": "Arc मेननेट · शृंखला 5042",
  "launch.cap": "जीनोम {seed} · कार्ड प्लेट",
  "card.claim": "पंजी दावा · शृंखला-बाह्य सत्यापन",
  "stage.live": "सजीव · टिक {n}",
  "home.sub": "Drosophila melanogaster · ऑनचेन पहला फ्लाई-ब्रेन",
  "home.neurons": "न्यूरॉन",
  "bd.cellTypes": "कोशिका प्रकार",
  "tl.aria": "तंत्रिका टेलीमेट्री",
  "tl.readout": "रीडआउट · p0–p3",
  "tl.readoutS": "सीमा {n} के सापेक्ष",
  "tl.raster": "स्पाइक रास्टर",
  "tl.rasterS": "{n} विचार · {c} कोशिकाएँ सक्रिय",
  "tl.rasterFoot": "x = विचार · y = संवेदी कोशिका 0–255",
  "tl.senses": "संवेदी उपकला",
  "tl.sensesS": "60 चैनलों पर {n} फ़ीड",
  "tl.lastFeed": "पिछली फ़ीड",
  "tl.channel": "चैनल {n}",
  "tl.heart": "Arc धड़कन",
  "tl.heartS": "हर ब्लॉक, आते ही",
  "tl.block": "ब्लॉक",
  "tl.txs": "लेन-देन",
  "tl.rate": "ब्लॉक / मिनट",
  "tl.neuron": "न्यूरॉन विवरण",
  "tl.neuronS": "नवीनतम विचार की सक्रिय कोशिकाएँ",
  "tl.rootId": "रूट आईडी",
  "tl.type": "कोशिका प्रकार",
  "tl.class": "वर्ग",
  "tl.super": "महावर्ग",
  "tl.where": "क्षेत्र · पक्ष",
  "tl.degree": "बाहर · भीतर",
  "tl.census": "कोशिका जनगणना",
  "tl.censusS": "सबसे बड़े BANC वर्ग · विचार सक्रिय करे तो रोशन",
  "tl.sign": "सिनैप्स चिह्न",
  "tl.signS": "उत्कीर्ण भार: उत्तेजक बनाम निरोधक",
  "tl.exc": "उत्तेजक",
  "tl.inh": "निरोधक",
  "tl.mag": "औसत |w|",
  "tl.scope": "फ़ायरिंग ऑसिलोस्कोप",
  "tl.spikes": "स्पाइक",
  "tl.synapses": "सिनैप्स",
  "tl.perThought": "प्रति विचार",
  "tl.mean": "औसत स्पाइक",
  "tl.peak": "शिखर",
  "tl.ratio": "सिनैप्स / स्पाइक",
  "bd.feedTag": "फ़ीड · चैनल {n}",
  "bd.strip": "मस्तिष्क {addr} · D. MELANOGASTER · ब्लॉक {block}",
  "bd.step1": "कनेक्ट",
  "bd.step2": "छँटाई",
  "bd.step3": "क्वांटाइज़",
  "bd.step4": "उत्कीर्ण",
  "bd.step5": "सील",
  "bd.step6": "सोच",
  "bd.stagesK": "छह चरण · मक्खी का मस्तिष्क कैसे ऑनचेन पहुँचा, एक-एक कदम",
  "bd.stageChip": "चरण {n} / 06",
  "bd.st1": "कनेक्टोम",
  "bd.st2": "छँटाईकार",
  "bd.st3": "क्वांटाइज़र",
  "bd.st4": "उत्कीर्णक",
  "bd.st5": "सीलर",
  "bd.st6": "विचारक",
  "bd.run": "चालू",
  "bd.v1": "{n} कोशिकाएँ",
  "bd.v2": "{n} कड़ियाँ",
  "bd.v3": "{n} B / कड़ी",
  "bd.v4": "{n} टेप",
  "bd.v5": "θ {n}",
  "bd.v6": "टिक {n}",
  "bd.waiting": "प्रतीक्षा",
  "bd.done": "/ पूर्ण",
  "bd.r1": "› BANC v888 पढ़ रहे हैं",
  "bd.r2": "› शीर्ष दो रख रहे हैं",
  "bd.r3": "› 5-बाइट रिकॉर्ड पैक कर रहे हैं",
  "bd.r4": "› टेप उत्कीर्ण कर रहे हैं",
  "bd.r5": "› वायरिंग सील कर रहे हैं",
  "bd.r6": "› Arc पर सोच रहे हैं",
  "bd.modelK": "मक्खी मॉडल · एक फल मक्खी, <mark>हर न्यूरॉन</mark> · सजीव",
  "bd.locked": "लॉक",
  "bd.zooming": "ज़ूम हो रहा",
  "bd.closeUp": "क्लोज़-अप",
  "bd.stop": "पड़ाव {n} / 6",
  "bd.w.specimen": "पूरा नमूना · परिक्रमा",
  "bd.ws.specimen": "D. melanogaster मस्तिष्क · BANC v888 · काँच क्यूटिकल",
  "bd.w.eyes": "संयुक्त आँखें · सतह",
  "bd.ws.eyes": "01 कनेक्ट · इंद्रियाँ आती हैं",
  "bd.w.cuticle": "क्यूटिकल के पार · काँच",
  "bd.ws.cuticle": "02 छँटाई · शरीर पारदर्शी",
  "bd.w.brain": "मक्खी के भीतर · कनेक्टोम",
  "bd.ws.brain": "03 क्वांटाइज़ · हर कोशिका BANC के स्थान पर",
  "bd.w.optic": "ऑप्टिक लोब · दृष्टि",
  "bd.ws.optic": "04 उत्कीर्ण · 85 टेपों में वायरिंग",
  "bd.w.memory": "मशरूम बॉडी · स्मृति",
  "bd.ws.memory": "05 सील · वायरिंग अब बदल नहीं सकती",
  "bd.w.map": "वापस · पूरी मक्खी",
  "bd.ws.map": "06 सोच · हर फ़ीड, Arc पर एक विचार",
  "bd.sn.specimen": "पूरा नमूना",
  "bd.sn.eyes": "संयुक्त आँखें",
  "bd.sn.cuticle": "क्यूटिकल",
  "bd.sn.brain": "पूरा मस्तिष्क",
  "bd.sn.optic": "ऑप्टिक लोब",
  "bd.sn.memory": "मशरूम बॉडी",
  "bd.sn.map": "पूरा नमूना",
  "bd.c.eye": "संयुक्त आँख",
  "bd.c.eyeS": "{n} फ़ोटोरिसेप्टर · BANC",
  "bd.c.antenna": "एंटीना · गंध",
  "bd.c.brain": "केंद्रीय मस्तिष्क",
  "bd.c.brainS": "{n} न्यूरॉन · {s} सिनैप्स",
  "bd.c.optic": "ऑप्टिक लोब",
  "bd.c.opticS": "{n} कोशिकाएँ · गति · रंग",
  "bd.c.memory": "मशरूम बॉडी",
  "bd.c.memoryS": "{n} केनयन कोशिकाएँ · स्मृति",
  "bd.bannerK": "एक फल मक्खी · एक मन",
  "bd.bannerT": "{n} न्यूरॉन।",
  "bd.bannerS": "हर एक, ऑनचेन।",
  "bd.axis": "गहराई",
  "bd.ax.surface": "सतह",
  "bd.ax.eyes": "आँखें",
  "bd.ax.cuticle": "क्यूटिकल",
  "bd.ax.brain": "मस्तिष्क",
  "bd.ax.optic": "ऑप्टिक",
  "bd.ax.memory": "स्मृति",
  "bd.ax.map": "मानचित्र",
  "bd.ax.deeper": "और गहरा",
  "bd.m1": "स्पाइक · सिनैप्स",
  "bd.m2": "स्थिति",
  "bd.m3": "पारदर्शिता",
  "bd.m4": "वर्ग · कोशिका प्रकार",
  "bd.m5": "पैरालैक्स",
  "bd.m6": "ब्लॉक",
  "bd.actK": "गतिविधि · क्षेत्रवार",
  "bd.actS": "{n} विचार",
  "bd.actChip": "सजीव · टिक {n}",
  "bd.g1": "ऑप्टिक लोब",
  "bd.g2": "एंटेनल लोब",
  "bd.g3": "मशरूम बॉडी",
  "bd.g4": "केंद्रीय परिसर",
  "bd.g5": "पार्श्व शृंग",
  "bd.g6": "अवरोही · VNC",
  "bd.actFrom": "टिक {n}",
  "bd.now": "अभी",
  "bd.actFoot": "पंक्ति = क्षेत्र · स्पाइक = एक विचार में सक्रिय कोशिकाएँ",
  "bd.conK": "संपर्क मानचित्र · कौन से क्षेत्र बात करते हैं",
  "bd.conS": "{n} सिनैप्स",
  "bd.pair": "जोड़ी {p}",
  "bd.hot": "सक्रिय जोड़ी",
  "bd.hotS": "{n} सिनैप्स",
  "bd.conFoot": "खाना = दो क्षेत्रों के बीच सिनैप्स · 85 टेपों से पढ़ा",
  "bd.rec": "रिक",
  "bd.obsS": "सजीव मक्खी · 3D स्कोप",
  "bd.watching": "देख रहे हैं · {s}",
  "bd.turn": "घुमाव {n}° · टिक {t}",
  "bd.footL": "एक मक्खी, एक <b>मस्तिष्क</b> · हर न्यूरॉन ऑनचेन, हर विचार एक ब्लॉक",
  "bd.footR1": "OBRAIN · ऑनचेन कनेक्टोम",
  "bd.footR2": "रेंडर किया नमूना · मस्तिष्क डेटा Arc से पढ़ा",
  "home.synapses": "सिनैप्स",
  "home.obs": "निरीक्षण में",
  "tape.tick":
    "टिक <b>{tick}</b> · विचार <b>#{thoughts}</b> · <b>{fired}</b> फायर · <b>{burned}</b> OBRAIN दहन",
  "tape.fired": "टिक <b>{tick}</b> · <b>{fired}</b> न्यूरॉन फायर",
  "tape.unreachable": "INDEXER अगम्य · टेप शांत है",
  "wallet.connected": "जुड़ा · तोड़ने के लिए क्लिक करें",
  "wallet.missing": "इस ब्राउज़र में कोई वॉलेट नहीं मिला",
  "bld.replay.p2":
    'BrainState घटना गणनाएँ ले जाती है; फायर id यहाँ पुनर्गणित होते हैं, और यही तरीका है जिससे किसी दावे का "यह विचार इस न्यूरॉन को फायर करता था" बिना किसी पर भरोसा किए जाँचा जाता है.',
};

const DICT: Readonly<Record<Lang, Dict>> = { en, zh, vi, hi };

// ---- the language preference ------------------------------------------------

/** Verifies the claim; a lying guard would hide corrupt storage behind a type. */
export function isLang(value: unknown): value is Lang {
  return value === "en" || value === "zh" || value === "vi" || value === "hi";
}

/** Reads the stored choice, defaulting to English (null store = private mode). */
export function readLang(store: LangStore | null): Lang {
  if (store === null) return "en";

  try {
    const value = store.getItem(LANG_KEY);

    return isLang(value) ? value : "en";
  } catch {
    return "en";
  }
}

/** Stores the choice; storage is a preference, not a requirement. */
export function storeLang(store: LangStore | null, lang: Lang): void {
  if (store === null) return;

  try {
    store.setItem(LANG_KEY, lang);
  } catch {
    // private modes refuse storage; the switch still works for this page view
  }
}

let current: Lang = "en";

export function setCurrent(lang: Lang): void {
  current = lang;
}

export function currentLang(): Lang {
  return current;
}

// ---- rendering --------------------------------------------------------------

type Params = Record<string, string>;

function interpolate(template: string, params: Params | undefined): string {
  if (params === undefined) return template;

  let value = template;

  for (const [name, replacement] of Object.entries(params))
    value = value.replaceAll(`{${name}}`, replacement);

  return value;
}

/** The copy for one key under an explicit language (pure: safe in tests). */
export function tr(lang: Lang, key: TKey, params?: Params): string {
  return interpolate(DICT[lang][key], params);
}

/** The copy for one key under the current language. */
export function t(key: TKey, params?: Params): string {
  return tr(current, key, params);
}

function isKey(value: string): value is TKey {
  return value in DICT.en;
}

/**
 * A surface that owns its own dictionary (the Game plate) registers how its
 * keys resolve: applyStatic then fills that surface's marked elements too,
 * without this file having to know a word of them.
 */
export type KeyFallback = (lang: Lang, key: string) => string | null;

let keyFallback: KeyFallback | null = null;

export function setKeyFallback(lookup: KeyFallback): void {
  keyFallback = lookup;
}

/** The copy for one static key: this dictionary's, or the registered surface's. */
function staticCopy(key: string): string | null {
  if (isKey(key)) return t(key);

  return keyFallback === null ? null : keyFallback(current, key);
}

/**
 * Fills every data-i18n-marked element in the subtree from the current
 * language: data-i18n as text, data-i18n-html as markup, and the two
 * attribute forms for title and aria-label.
 */
export function applyStatic(root: ParentNode = document): void {
  for (const el of root.querySelectorAll<HTMLElement>("[data-i18n]")) {
    const key = el.dataset.i18n;
    const copy = key === undefined ? null : staticCopy(key);

    if (copy !== null) el.textContent = copy;
  }

  for (const el of root.querySelectorAll<HTMLElement>("[data-i18n-html]")) {
    const key = el.dataset.i18nHtml;
    const copy = key === undefined ? null : staticCopy(key);

    if (copy !== null) el.innerHTML = copy;
  }

  for (const el of root.querySelectorAll<HTMLElement>("[data-i18n-title]")) {
    const key = el.dataset.i18nTitle;
    const copy = key === undefined ? null : staticCopy(key);

    if (copy !== null) el.setAttribute("title", copy);
  }

  for (const el of root.querySelectorAll<HTMLElement>("[data-i18n-arialabel]")) {
    const key = el.dataset.i18nArialabel;
    const copy = key === undefined ? null : staticCopy(key);

    if (copy !== null) el.setAttribute("aria-label", copy);
  }

  for (const el of root.querySelectorAll<HTMLElement>("[data-i18n-alt]")) {
    const key = el.dataset.i18nAlt;
    const copy = key === undefined ? null : staticCopy(key);

    if (copy !== null) el.setAttribute("alt", copy);
  }
}
